/**
 * A range of the document as Word hands it to the add-in: a `pkg:package`, a
 * `w:document`, or a bare run of paragraphs. `OoxmlRange` parses it once and
 * answers the questions the proposal, the anchor tracker and the web routes
 * ask of it: which body paragraphs it holds, their accepted text and identity,
 * which span of each paragraph a set of bookmarks covers, and where a set of
 * bookmarks lands in a full document. It also owns the two mutations the
 * proposal builder makes, replacing a paragraph and inserting a new one, so
 * the DOM never leaves this class.
 *
 * Coordinates are accepted text (all tracked changes accepted), matching the
 * paragraph engine; every text and offset comes from `ParagraphModel`, the
 * engine's own reading of a paragraph, so the two can never disagree.
 * Bookmark markers are read through `OoxmlBookmarks`, the one place that
 * pairs a `bookmarkStart` with its `bookmarkEnd`.
 */
import { ClauseMarker } from "./text/clause-marker";
import type { DocumentParagraph, ParagraphLink, ParagraphPendingChange, TableOfContentsEntry, UneditableSpan } from "./wire/document-paragraph";
import type { DocumentComments } from "./ooxml/document-comments";
import { DocumentHeadings } from "./ooxml/document-headings";
import type { OoxmlNumbering } from "./ooxml/ooxml-numbering";
import type { OoxmlOutlineLevels } from "./ooxml/ooxml-outline-levels";
import { wordDiff, type WordDiffHunk } from "./text/word-diff";
import { dropHalfAnchoredCommentMarkers } from "./ooxml/half-anchored-comments";
import { hyperlinkTargetsOf, type HyperlinkTargetsByRelationshipId } from "./ooxml/hyperlink-targets";
import { OoxmlBookmarks } from "./ooxml/ooxml-bookmarks";
import {
    DELETION_CONTAINERS,
    FRAGMENT_NAMESPACE_DECLARATIONS,
    INSERTION_CONTAINERS,
    elementNameWithoutPrefix,
    PACKAGE_NS,
    PROPERTY_CHANGE_TAGS,
    REVISION_TAGS,
    TEXT_TAGS,
    WORDML_2010_NS,
    WORDPROCESSINGML_NS,
} from "./ooxml/ooxml-names";
import { PAGE_BODY_CONTENT_MARKER, PackageBinaryParts, PackageMediaForPages } from "./ooxml/package-binary-parts";
import { freshParagraphBookmarkName, isParagraphBookmarkName } from "./ooxml/paragraph-bookmark-naming";
import { ParagraphLinks } from "./ooxml/paragraph-links";
import { ParagraphModel } from "./ooxml/paragraph-model";
import { ParagraphRevisionMarks } from "./ooxml/paragraph-revision-marks";
import { TableOfContents, type TableOfContentsEntryTarget } from "./ooxml/table-of-contents";
import { TableStructure } from "./ooxml/table-structure";
import { complexFieldWalkOf } from "./ooxml/word-owned-text-unlinker";
import type { AcceptedTextSpan } from "./redline-engine";
import { Revision } from "./ooxml/revision";
import { WordTokenizer } from "./text/word-tokenizer";
import { XmlHost } from "./xml-host";

/** Word's per-paragraph identity, used to tell whether the range still holds the same paragraphs between reads. */
export interface ParagraphIds {
    paraId: string | null;
    textId: string | null;
}

/** A word to move a bookmark edge to, identified by its text and which occurrence of that text inside the span it is. */
export interface BookmarkEdgeWord {
    text: string;
    occurrenceIndex: number;
}

/** One paragraph's bookmark span after neighbour text has been trimmed off, with the words the bookmark edges should move to. */
export interface TrimmedBookmarkSpan {
    span: AcceptedTextSpan;
    startAfterWord: BookmarkEdgeWord | null;
    endBeforeWord: BookmarkEdgeWord | null;
}

/** The words at one end of a bookmark span that a neighbouring paragraph's text accounts for. */
interface SwallowedNeighbourWords {
    /** The span's words from that end the trim would take off: the neighbour's words and any live words between them. */
    swallowedWordCount: number;
    /** Of those, the words the neighbour does not hold: words typed or changed in it since, or the redline's own words a match ran across. */
    wordsNotInNeighbourCount: number;
}

/** No neighbour text at this end of the span. */
const NOTHING_SWALLOWED: SwallowedNeighbourWords = { swallowedWordCount: 0, wordsNotInNeighbourCount: 0 };

/** How many words the trim takes off each end of a span. */
interface NeighbourWordsToTrim {
    leadingNeighbourWords: number;
    trailingNeighbourWords: number;
}

/** The two ends' matches, weighed against the span they were found in. */
interface SwallowedAtBothEnds {
    swallowedAtStart: SwallowedNeighbourWords;
    swallowedAtEnd: SwallowedNeighbourWords;
    spanWordCount: number;
}

export interface InsertedParagraphRequest {
    /** The paragraph to insert after; null puts the new paragraph at the front of the body. */
    afterParagraph: Element | null;
    sourceParagraphXml: string;
    revision: Revision;
}

/** A page cut out of a whole-document read: a set of bookmarks' paragraphs in the package shape Word's own export of their range has. */
export interface BookmarkedPageCut {
    kind: "page";
    packageOoxml: string;
}

/**
 * Bookmarks whose paragraphs are not plain body paragraphs: a table cell, which Word
 * exports as its whole row inside the table, or a paragraph inside a block-level content
 * control. Word shapes those exports itself, so `pageOf` cuts no span over them: a table
 * group's page is cut paragraph by paragraph (`tablePageOf`), and the rest are asked of Word.
 */
export interface BookmarkedPageReadFromWord {
    kind: "readFromWord";
}

/** What one whole-document read gives for a set of bookmarks (`BookmarkedPages.pageOf`). */
export type BookmarkedPage = BookmarkedPageCut | BookmarkedPageReadFromWord;

/**
 * A table group's page cut out of a whole-document read: the paragraph each listed
 * bookmark names, alone, in the document's order, each once, in one package. It is the
 * page the add-in builds from Word's read of each of those paragraphs on its own
 * (`tablePageReadOneByOne`), made without a Word read.
 */
export interface BookmarkedTablePageCut {
    kind: "tablePage";
    packageOoxml: string;
    /**
     * The row each listed bookmark's paragraph sits in, as the read's markup places it:
     * the innermost cell's table and row numbers (`TableStructure.cellPathOf`), "2:3".
     *
     * NULL
     *   A bookmark whose paragraph sits outside every table has no entry.
     */
    cellRowKeyByBookmarkName: Map<string, string>;
}

/** What one whole-document read gives for a table group's bookmarks (`BookmarkedPages.tablePageOf`). */
export type BookmarkedTablePage = BookmarkedTablePageCut | BookmarkedPageReadFromWord;

/** The page Apply reads a card or a group on, as one whole-document read gives it (`BookmarkedPages.applyPageOf`). */
export type BookmarkedApplyPage = BookmarkedPageCut | BookmarkedTablePageCut | BookmarkedPageReadFromWord;

/** Word's export of a range as every reader of it gets it (`OoxmlRange.readOfWordExport`). */
export interface WordExportRead {
    /** The export without the empty paragraph Word ends it with, or as Word gave it where its paragraphs cannot be paired with Word's. */
    packageOoxml: string;
    /**
     * Whether `packageOoxml` is Word's export as given, its paragraphs not paired with
     * Word's (`paragraphCountLiningUpWithWord` null): the empty paragraph Word ends it
     * with is still in it wherever Word wrote one, so a write over it adds none
     * (`withWordsClosingParagraph`).
     */
    wordsExportAsGiven: boolean;
}

/**
 * Pages for any number of bookmark sets out of one whole-document read
 * (`OoxmlRange.bookmarkedPagesOf`), so Apply All reads Word once a run instead of once a card.
 */
export interface BookmarkedPages {
    /**
     * The page Word's export of these bookmarks' paragraphs gives, without the empty
     * paragraph Word ends that export with (`bookmarkedPage`).
     *
     * NULL
     *   none of the bookmarks is in the read, as Word's read of their range returns.
     */
    pageOf(bookmarkNames: string[]): BookmarkedPage | null;
    /**
     * The page of a group whose listed bookmarks include a table cell's: each listed
     * bookmark's paragraph on its own, and the row of each listed cell
     * (`OoxmlRange.bookmarkedTablePage`).
     */
    tablePageOf(listedBookmarkNames: string[]): BookmarkedTablePage;
    /**
     * The page Apply reads a card or a group on: the span's page when the bookmarks'
     * paragraphs are body paragraphs (`pageOf`), else the cells' page of every listed
     * bookmark (`tablePageOf`), else what the read leaves to Word. The one answer Apply
     * All's page reader asks for.
     *
     * NULL
     *   none of the bookmarks is in the read, as Word's read of their range returns.
     */
    applyPageOf(bookmarkNames: string[], listedBookmarkNames: string[]): BookmarkedApplyPage | null;
    /**
     * Whether the read holds any of these bookmarks on one of its paragraphs, a table cell's
     * paragraph included: a card on them has a page here (`applyPageOf` is not null), as
     * Word's read of their range finds the card while any one of them stands.
     */
    holdsAnyOf(bookmarkNames: string[]): boolean;
    /**
     * The bookmarks of these that the read holds and that do not start and end inside one
     * paragraph, each asked on its own: the ones Word re-lays (`repairEdges`) before Apply
     * writes over them. A bookmark the read does not hold is not among them.
     */
    unsettledBookmarkNamesOf(bookmarkNames: string[]): string[];
}

/** Where one bookmark marker sits among the body paragraphs of a read. */
interface BookmarkMarkerPosition {
    /** The body paragraph the marker is inside; null for a marker at body level, outside every paragraph. */
    enclosingParagraphIndex: number | null;
    /** How many body paragraphs began before the marker: for a body-level marker, the index of the paragraph after it. */
    paragraphsBegunBefore: number;
    /** Text elements (`w:t`, `w:delText`) of the enclosing paragraph read before the marker; 0 at body level. */
    textElementsBefore: number;
}

/** A bookmark start as the walk of the body met it, before its end is paired with it by id. */
interface BookmarkStartSeen {
    bookmarkName: string;
    bookmarkId: string;
    position: BookmarkMarkerPosition;
}

/** A bookmark's two markers; `end` is null when the read holds no end with the start's id. */
interface BookmarkMarkerPair {
    start: BookmarkMarkerPosition;
    end: BookmarkMarkerPosition | null;
}

/** Every bookmark of a read, found in one walk of its body, and how many text elements each body paragraph holds. */
interface BookmarkMarkerIndex {
    markersByBookmarkName: Map<string, BookmarkMarkerPair>;
    textElementCountByParagraph: number[];
}

/** The first and last body paragraph a bookmark's range touches, by index into the read's body paragraphs. */
export interface BookmarkParagraphSpan {
    firstParagraphIndex: number;
    lastParagraphIndex: number;
}

/**
 * The read's package with its body content taken out, put back around each page's paragraphs,
 * with the media parts that page uses and no others (`PackageMediaForPages`).
 */
interface PackageAroundBodyContent {
    packageAroundPageBodyContent(pageBodyContent: string): string;
}

/** What every table page of one whole-document read is cut with: the paragraph each bookmark names, and the read's tables. */
interface TablePageCutIndex {
    paragraphElementByBookmarkName: Map<string, Element>;
    tableStructure: TableStructure;
}

/** A listed bookmark and the paragraph of the read it names. */
interface ListedBookmarkParagraph {
    bookmarkName: string;
    namedParagraph: Element;
}

/**
 * What `nameUnnamedParagraphs` did to the document: the retired-scheme names it
 * removed, in document order, and the names it minted, in paragraph order. Both
 * empty when the document was already named under the active scheme.
 */
export interface ParagraphNamingResult {
    removedLegacyNames: string[];
    addedNames: string[];
}

export class OoxmlRange {
    private readonly rangeDocument: Document;
    /** The body paragraphs as read; `replaceParagraph` swaps entries in place, inserted paragraphs are never added, so indexes stay stable for the builder. */
    private readonly paragraphElements: Element[];

    constructor(xml: string) {
        this.rangeDocument = OoxmlRange.parseRangeXml(xml);
        this.paragraphElements = OoxmlRange.bodyParagraphs(this.rangeDocument);
    }

    /** Minimal `pkg:package` around body XML: the shape `insertOoxml` accepts and `RedlinePreview` renders. */
    static packageAround(bodyXml: string): string {
        return `<pkg:package xmlns:pkg="${PACKAGE_NS}">
  <pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml" pkg:padding="512">
    <pkg:xmlData><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships></pkg:xmlData>
  </pkg:part>
  <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData><w:document xmlns:w="${WORDPROCESSINGML_NS}" xmlns:w14="${WORDML_2010_NS}"><w:body>${bodyXml}</w:body></w:document></pkg:xmlData>
  </pkg:part>
</pkg:package>`;
    }

    /**
     * Parses a whole package or a bare fragment; a fragment is wrapped with the namespaces Word's OOXML uses.
     *
     * WHY the embedded fonts are left out before the parse
     *   A document that embeds its fonts hands every read, a single paragraph's included,
     *   as 8 to 9 million characters of base64 font parts that no reader of this class
     *   looks at. Parsed, each read would hold and walk them, and every package built from
     *   it would carry them out again (`PackageBinaryParts.withoutEmbeddedFonts`).
     */
    private static parseRangeXml(rangeXml: string): Document {
        const fontStrippedRangeXml = PackageBinaryParts.withoutEmbeddedFonts(rangeXml);
        const xmlParser = XmlHost.domParser();
        const directParseDocument = xmlParser.parseFromString(fontStrippedRangeXml, "application/xml");
        /**
         * CASES
         *   - direct parse succeeded -> return the document as read
         *   - direct parse failed    -> try fragment recovery below
         */
        if (directParseDocument.getElementsByTagName("parsererror").length === 0) return directParseDocument;
        const xmlFragmentWithoutDeclaration = fontStrippedRangeXml.replace(/^\s*<\?xml[^>]*\?>/, "");
        const wrappedFragmentDocument = xmlParser.parseFromString(`<fragment ${FRAGMENT_NAMESPACE_DECLARATIONS.join(" ")}>${xmlFragmentWithoutDeclaration}</fragment>`, "application/xml");
        /**
         * WHEN the wrapped fragment parse still fails
         *   CONSEQUENCE
         *     Throw: the markup is not valid Word OOXML even as a paragraph fragment.
         */
        if (wrappedFragmentDocument.getElementsByTagName("parsererror").length > 0) {
            throw new Error("The redline could not be read as Word markup.");
        }
        return wrappedFragmentDocument;
    }

    private static bodyOf(doc: Document): Element {
        const bodyElements = doc.getElementsByTagNameNS(WORDPROCESSINGML_NS, "body");
        /**
         * CASES
         *   - at least one w:body found -> use bodies[0]
         *   - no w:body found           -> use doc.documentElement
         * EXAMPLE
         *   bodies.length = 1
         *   bodies[0]     = the document body
         *   return        -> bodies[0]
         */
        return bodyElements.length > 0 ? bodyElements[0] : doc.documentElement;
    }

    /**
     * Paragraphs of the body proper. Paragraphs inside text boxes sit inside
     * another `w:p`, and `mc:Fallback` carries a second copy of drawn content;
     * both are excluded so counts match what Word reports for the range.
     */
    private static bodyParagraphs(doc: Document): Element[] {
        const isNestedInParagraph = (paragraph: Element, root: Element): boolean => {
            for (let ancestorElement = paragraph.parentElement; ancestorElement && ancestorElement !== root; ancestorElement = ancestorElement.parentElement) {
                const ancestorElementName = elementNameWithoutPrefix(ancestorElement);
                const ancestorIsParagraph = ancestorElementName === "p";
                const ancestorIsFallback = ancestorElementName === "Fallback";
                const ancestorIsNestedDuplicateContainer = ancestorIsParagraph || ancestorIsFallback;
                /**
                 * WHEN an ancestor is w:p or mc:Fallback
                 *   CONSEQUENCE
                 *     Treat this paragraph as nested duplicate content, not a top-level body paragraph.
                 */
                if (ancestorIsNestedDuplicateContainer) return true;
            }
            return false;
        };

        const bodyRootElement = OoxmlRange.bodyOf(doc);
        const bodyRootElementName = elementNameWithoutPrefix(bodyRootElement);
        const bodyRootIsParagraph = bodyRootElementName === "p";
        const bodyRootIsWordprocessingElement = bodyRootElement.namespaceURI === WORDPROCESSINGML_NS;
        const bodyRootIsWordprocessingParagraph = bodyRootIsParagraph && bodyRootIsWordprocessingElement;
        /**
         * WHEN the parsed root is itself a w:p
         *   CONSEQUENCE
         *     Return a one-paragraph list instead of searching for paragraphs inside a body.
         */
        if (bodyRootIsWordprocessingParagraph) return [bodyRootElement];
        return Array.from(bodyRootElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p")).filter((paragraph) => !isNestedInParagraph(paragraph, bodyRootElement));
    }

    get paragraphCount(): number {
        return this.paragraphElements.length;
    }

    /** A handle on one paragraph for positioning an insertion; callers never read or write through it. */
    paragraphElement(index: number): Element {
        return this.paragraphElements[index];
    }

    paragraphXml(index: number): string {
        return XmlHost.xmlSerializer().serializeToString(this.paragraphElements[index]);
    }

    /** The accepted text of every body paragraph, in order. */
    allChangesAcceptedTexts(): string[] {
        return this.paragraphElements.map((paragraph) => new ParagraphModel(paragraph).text);
    }

    /**
     * The text of every body paragraph with its deleted words still in it, in order:
     * what `Paragraph.text` reads for the paragraph under markup, where a struck word
     * is read as present. The pairing of Word's paragraph list with this walk compares
     * the two (`OfficeWordSession`).
     *
     * EXAMPLE
     *   "the " + struck "cat" + inserted "dog" + " is big" -> "the catdog is big"; `allChangesAcceptedTexts` -> "the dog is big"
     */
    textsWithDeletedWordsKept(): string[] {
        return this.paragraphElements.map((paragraph) => new ParagraphModel(paragraph).pieces.map((piece) => piece.text).join(""));
    }

    /**
     * Whether any body paragraph carries a tracked insertion or deletion in its runs.
     *
     * WHY
     *   After a package with revision marks is written into a table cell, the cell is
     *   read back to see whether Word kept the marks; a cell that lost them is rewritten
     *   under Word's own tracking.
     */
    hasRevisionMarks(): boolean {
        return this.paragraphElements.some((paragraph) => new ParagraphModel(paragraph).pieces.some((piece) => piece.insertedBy !== null || piece.deletedBy !== null));
    }

    /**
     * Whether any body paragraph holds a field or a hyperlink: a complex field by the
     * one field walk (`complexFieldWalkOf`), a `w:fldSimple`, or a `w:hyperlink`.
     *
     * WHY
     *   Word for Mac reports a range inside any of these with the field's code in it
     *   (the hyperlink's "HYPERLINK …" between Word's field characters and its display
     *   text; live hunt, 2026-10-16), so the cell writer places such a paragraph's words
     *   by Word's search over this reader's text instead of by Word's ranges.
     *
     * EXAMPLE
     *   <w:p><w:r><w:t>see the </w:t></w:r><w:hyperlink w:anchor="_fees"><w:r><w:t>Fee Schedule</w:t></w:r></w:hyperlink></w:p> -> true
     *   <w:p><w:r><w:t>18% of the fee</w:t></w:r></w:p>                                                                            -> false
     */
    holdsFieldsOrHyperlinks(): boolean {
        return this.paragraphElements.some((paragraph) =>
            complexFieldWalkOf(paragraph).fields.length > 0
            || paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "fldSimple").length > 0
            || paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "hyperlink").length > 0);
    }

    /**
     * How many of this export's paragraphs, from the first, are the document's own, lined
     * up with Word's list of them: the one rule by which every read of Word leaves out the
     * empty paragraph Word ends its export with (`readOfWordExport`,
     * `documentParagraphsOfWordExport`, the whole-body read's views).
     *
     * WHY Word's count, never the paragraph's text
     *   `getOoxml()` ends the package it returns with one paragraph past the range's last,
     *   and the body's with one past the body's last (Word for Mac, 2026-10-15: 227
     *   paragraphs in the body's package, 226 in `Body.paragraphs`). An empty paragraph of
     *   the document reads exactly like Word's: told apart by its text, a cap paragraph the
     *   user had struck whole was taken for Word's, and the card's sentence was written on
     *   Word's paragraph. The count says which paragraph is Word's: the last.
     *
     * WHY that last paragraph must hold no text, kept or deleted
     *   Word's own holds none. A last paragraph that holds text is the document's, and the
     *   export and Word list different paragraphs; left out of a read, its words would be
     *   missing from the package Apply writes over them, and gone from the document with no
     *   tracked change.
     *
     * CASES, for the export's paragraph count against Word's
     *   - the same                               -> all of them: Word added none
     *   - one more, the last holding no text     -> all but the last, which is Word's
     *   - one more, the last holding text        -> null: no paragraph can be paired with Word's
     *   - anything else                          -> null: a cell Word reads as its whole row
     *
     * EXAMPLE
     *   Word 1,   export [the cap paragraph, its sentence struck whole; Word's]  -> 1, the cap paragraph
     *   Word 226, export 227, the last holding nothing                           -> 226
     *   Word 1,   export ["Fees are due.", "Schedule A"]                         -> null
     *   Word 1,   export 4 (a row of three cells, then Word's)                   -> null
     */
    paragraphCountLiningUpWithWord(wordParagraphCount: number): number | null {
        /** WHEN the export holds Word's paragraphs and no more CONSEQUENCE every one is the document's. */
        if (this.paragraphCount === wordParagraphCount) return wordParagraphCount;
        /**
         * EXAMPLE
         *   Word 226 -> 226 + 1 = 227 export paragraphs, the last at index 227 - 1 = 226
         */
        const exportHoldsOneMore = this.paragraphCount === wordParagraphCount + 1;
        const exportEndsWithWordsParagraph = exportHoldsOneMore && this.lastParagraphHoldsNoText();
        /**
         * CASES
         *   - the export ends with Word's empty paragraph -> Word's count
         *   - it does not                                 -> null
         */
        return exportEndsWithWordsParagraph ? wordParagraphCount : null;
    }

    /**
     * How many paragraphs of Word's export as given, from the first, come before the empty
     * paragraph Word ends it with: the paragraphs a card placed beside one can go beside.
     *
     * WHY
     *   An export Word's count cannot pair (`paragraphCountLiningUpWithWord` null) is read as
     *   given, Word's paragraph in it (`WordExportRead.wordsExportAsGiven`). A card placed after
     *   the read's last paragraph went after Word's, and Word's empty paragraph stayed in the
     *   document as an empty line above the card's.
     *
     * CASES
     *   - the last paragraph holds no text, kept or deleted -> all but the last, which is Word's
     *   - it holds text                                     -> all of them: the last is the document's
     *
     * EXAMPLE
     *   export ["7.9 Either party may renew.", Word's]  -> 1
     *   export ["Fees are due.", "Schedule A"]          -> 2
     */
    paragraphCountBeforeWordsClosingParagraph(): number {
        /**
         * EXAMPLE
         *   2 export paragraphs, the last Word's -> 2 - 1 = 1
         */
        return this.lastParagraphHoldsNoText() ? this.paragraphCount - 1 : this.paragraphCount;
    }

    /**
     * Whether the export's last paragraph holds no text, kept or deleted: how the empty
     * paragraph Word ends its export with reads, and never a paragraph of the document that
     * holds words, struck or not.
     *
     * NULL
     *   An export with no paragraph has no last one: false.
     */
    private lastParagraphHoldsNoText(): boolean {
        /** WHEN the export holds no paragraph CONSEQUENCE none is Word's. */
        if (this.paragraphCount === 0) return false;
        /**
         * EXAMPLE
         *   227 export paragraphs -> the last at index 227 - 1 = 226
         */
        return new ParagraphModel(this.paragraphElements[this.paragraphCount - 1]).pieces.every((piece) => piece.text === "");
    }

    /**
     * Word's export of a range as every reader of it gets it: the empty paragraph Word
     * ends it with left out (`paragraphCountLiningUpWithWord`), every other part of the
     * package kept but the embedded fonts (`PackageBinaryParts.withoutEmbeddedFonts`), the
     * read a card is built over and written back from, whose links and numbering resolve
     * through those parts.
     *
     * CASES
     *   - the export ends with Word's paragraph      -> the export without it
     *   - it holds the range's paragraphs alone      -> the export, untouched
     *   - it cannot be paired with Word's paragraphs
     *     (a cell read as its row)                   -> the export as Word gave it, untouched, marked
     *                                                   so (`WordExportRead.wordsExportAsGiven`)
     *
     * WHY every case drops the embedded fonts
     *   The packages built over this read go to the merge writer, and a font part in one
     *   is 1.6 million characters the write never needs: the document keeps its own.
     *
     * EXAMPLE
     *   Word's range   the cap paragraph alone, its sentence struck whole by another author
     *   export         <w:p>…$1.00 USD.…</w:p><w:p><w:pPr><w:sectPr/></w:pPr></w:p>
     *   -> <w:p>…$1.00 USD.…</w:p>, not Word's export as given
     */
    static readOfWordExport(exportOoxml: string, wordParagraphCount: number): WordExportRead {
        const exportWithoutEmbeddedFonts = PackageBinaryParts.withoutEmbeddedFonts(exportOoxml);
        const exportRead = new OoxmlRange(exportWithoutEmbeddedFonts);
        const documentParagraphCount = exportRead.paragraphCountLiningUpWithWord(wordParagraphCount);
        /** WHEN Word's paragraphs cannot be told from the document's CONSEQUENCE Word's own text without its fonts, marked as such. */
        if (documentParagraphCount === null) return { packageOoxml: exportWithoutEmbeddedFonts, wordsExportAsGiven: true };
        /** WHEN Word added no paragraph CONSEQUENCE Word's own text without its fonts: it is the document's paragraphs alone. */
        if (documentParagraphCount === exportRead.paragraphCount) return { packageOoxml: exportWithoutEmbeddedFonts, wordsExportAsGiven: false };
        /**
         * EXAMPLE
         *   2 export paragraphs -> index `2 - 1` = 1, Word's, the last
         */
        const wordsClosingParagraph = exportRead.paragraphElements[exportRead.paragraphCount - 1];
        wordsClosingParagraph.parentNode?.removeChild(wordsClosingParagraph);
        return { packageOoxml: exportRead.serialize(), wordsExportAsGiven: false };
    }

    /**
     * Word's export of a range as its document paragraphs alone in a package, the empty
     * paragraph Word ends it with left out (`paragraphCountLiningUpWithWord`): the read a
     * page keeps of one bookmark's paragraph, whose readers take the paragraph's words and
     * revisions and none of the package's other parts.
     *
     * CASES
     *   - the export lines up with Word's paragraphs -> those paragraphs, in a package of their own
     *   - it cannot be paired with them              -> the export as Word gave it, without its embedded fonts
     *
     * EXAMPLE
     *   Word's range   one paragraph, "Customer shall pay within thirty (30) days."
     *   export         that paragraph, then Word's empty one, with styles, numbering and theme parts
     *   -> a package of that paragraph alone
     */
    static documentParagraphsOfWordExport(exportOoxml: string, wordParagraphCount: number): string {
        const exportWithoutEmbeddedFonts = PackageBinaryParts.withoutEmbeddedFonts(exportOoxml);
        const exportRead = new OoxmlRange(exportWithoutEmbeddedFonts);
        const documentParagraphCount = exportRead.paragraphCountLiningUpWithWord(wordParagraphCount);
        /** WHEN Word's paragraphs cannot be told CONSEQUENCE Word's own text, without its embedded fonts. */
        if (documentParagraphCount === null) return exportWithoutEmbeddedFonts;
        const xmlSerializer = XmlHost.xmlSerializer();
        /**
         * EXAMPLE
         *   Word 1 -> slice(0, 1): the export's first paragraph, the document's
         */
        const documentParagraphXmls = exportRead.paragraphElements.slice(0, documentParagraphCount).map((documentParagraph) => xmlSerializer.serializeToString(documentParagraph));
        return OoxmlRange.packageAround(documentParagraphXmls.join(""));
    }

    /**
     * A package of the document's paragraphs as Word is given it over whole paragraphs:
     * with the empty paragraph Word ends its own export with put back after the last of
     * them, before the body's section properties.
     *
     * WHY
     *   Word's export ends with that paragraph, and so does every package Apply writes over
     *   whole paragraphs: written last, Word keeps nothing of it; written before another
     *   paragraph, it stays in the document as an empty one (chat 7ce752c9, cards #5 and #6,
     *   2026-10-21). Every read leaves it out, so its readers see the document's paragraphs
     *   alone, and the write puts it back, so Word is given the shape of its own export.
     *
     * EDGE CASE
     *   A package built over Word's export as given (`WordExportRead.wordsExportAsGiven`)
     *   still ends with Word's paragraph, and a second one would stay in the document as an
     *   empty line: its writer writes it as built, and never calls this.
     *
     * CASES
     *   - the body ends with section properties -> the paragraph goes just before them
     *   - it has none                           -> the paragraph ends the body
     *
     * EXAMPLE
     *   body  <w:p>Notice</w:p><w:p>Late fees accrue.</w:p><w:sectPr/>
     *   ->    <w:p>Notice</w:p><w:p>Late fees accrue.</w:p><w:p/><w:sectPr/>
     */
    static withWordsClosingParagraph(documentParagraphsPackageOoxml: string): string {
        const packageRead = new OoxmlRange(documentParagraphsPackageOoxml);
        const bodyElement = OoxmlRange.bodyOf(packageRead.rangeDocument);
        const wordsClosingParagraph = packageRead.rangeDocument.createElementNS(WORDPROCESSINGML_NS, "w:p");
        bodyElement.insertBefore(wordsClosingParagraph, TableStructure.childNamed(bodyElement, "sectPr"));
        return packageRead.serialize();
    }

    /**
     * The paragraph of a read that answers to `bookmarkName`, alone in a package.
     *
     * WHY
     *   `Paragraph.getOoxml()` on a table-cell paragraph returns the whole row as a
     *   one-row table, every cell of it, so "the first paragraph" of that read is the
     *   row's first cell. The bookmark the paragraph was found by sits in the cell
     *   that was asked for, and names it whatever column it is in.
     *
     * EXAMPLE
     *   read of the Fee cell (bookmark _fee) in the row  Platform subscription | per year | $120,000
     *     first paragraph       -> "Platform subscription"   (the Item cell)
     *     paragraph of _fee     -> "$120,000"                (the cell that was read)
     *
     * NULL
     *   No paragraph in the read answers to the name. A caller writing the paragraph
     *   back refuses rather than guess at a cell.
     */
    static paragraphAsPackageByBookmarkName(rangeOoxml: string, bookmarkName: string): string | null {
        const bookmarkedParagraph = new OoxmlRange(rangeOoxml).paragraphElementsByBookmarkName().get(bookmarkName);
        return bookmarkedParagraph === undefined ? null : OoxmlRange.packageAround(XmlHost.xmlSerializer().serializeToString(bookmarkedParagraph));
    }

    /**
     * The paragraph a read of ONE bookmarked paragraph was made for, alone in a
     * package, with the bookmark put back on it when Word dropped it.
     *
     * WHY
     *   A table cell's paragraph is only ever read on its own: Word exports a range that
     *   crosses table rows as its first row alone (live probe, Word for Mac 16.114,
     *   2026-10-16: rows 1-3 of a question table read as row 1's three cells, every
     *   other paragraph missing). One paragraph's read comes back in one of the shapes
     *   below, and each yields the paragraph the bookmark names.
     *
     * CASES, for the shapes Word was seen to read (live probes, 2026-10-15)
     *   - Word for Mac, a cell's last paragraph       -> the row: every cell, the bookmark in its own cell
     *   - Word for Mac, any other paragraph           -> the paragraph, then the package's closing paragraph
     *   - Word Online, an empty cell                  -> the paragraph alone, its zero-width bookmark dropped
     *
     * CASES, for what is returned
     *   - a paragraph of the read answers to the bookmark          -> that paragraph, alone in a package
     *   - the read holds no table cell and no paragraph answers    -> the bookmark is laid on the read's first
     *                                                                 paragraph, which is the one the read was
     *                                                                 made of, then as above
     *
     * WHY only a read without a table cell takes the bookmark back
     *   A row read holds a paragraph for every cell of the row, so its first paragraph
     *   may be another cell's; laying the bookmark there would name the wrong cell.
     *
     * NULL
     *   A row read in which no paragraph answers to the bookmark: the paragraph cannot be
     *   told from its row neighbours, and a caller refuses rather than guess at a cell.
     *
     * EXAMPLE
     *   Word Online read of the empty Annual cell   <w:p w:rsidR="00ED02A7"/>
     *   bookmark                                    _leg_letter_wire
     *   -> <w:p w:rsidR="00ED02A7"><w:bookmarkStart w:name="_leg_letter_wire"/><w:bookmarkEnd/></w:p>
     */
    static paragraphReadOfBookmark(paragraphReadOoxml: string, bookmarkName: string): string | null {
        const paragraphRead = new OoxmlRange(paragraphReadOoxml);
        /**
         * WHEN the read holds no table cell: the paragraph without its row
         *   CONSEQUENCE
         *     a bookmark Word dropped goes back on the read's first paragraph (index 0); a
         *     bookmark Word kept stays where Word put it (`restoreDroppedBookmarks`).
         */
        if (paragraphRead.holdsNoTableCell()) paragraphRead.restoreDroppedBookmarks(new Map([[bookmarkName, 0]]));
        return OoxmlRange.paragraphAsPackageByBookmarkName(paragraphRead.serialize(), bookmarkName);
    }

    /**
     * Whether the read holds at least one paragraph and none of them sits in a table
     * cell: the shape of one paragraph read without the row around it.
     *
     * RETURNS
     *   false for a row read, and for a read with no paragraph at all.
     */
    private holdsNoTableCell(): boolean {
        const paragraphSitsInTableCell = (paragraph: Element): boolean => {
            for (let ancestorElement = paragraph.parentElement; ancestorElement !== null; ancestorElement = ancestorElement.parentElement) {
                /** WHEN an ancestor is a w:tc CONSEQUENCE the paragraph is a cell's, read with its row. */
                if (ancestorElement.namespaceURI === WORDPROCESSINGML_NS && elementNameWithoutPrefix(ancestorElement) === "tc") return true;
            }
            return false;
        };
        return this.paragraphElements.length > 0 && this.paragraphElements.every((paragraph) => !paragraphSitsInTableCell(paragraph));
    }

    /**
     * Pages for bookmark sets out of one whole-document read (`Body.getOoxml`), the read
     * Apply All makes once a run in place of one Word read a card.
     *
     * WHY a page is cut rather than asked of Word
     *   Every Word read is a round trip of about 150–220 ms whatever its size, and reading
     *   one bookmark's paragraphs took three. A whole-document read is one round trip:
     *   0.12–0.33 s for 50 to 3 000 paragraphs, against about 0.46 s for one paragraph read
     *   the old way. 27 cards read one by one held Apply All for over 30 s.
     *
     * WHY the read is parsed once and kept private
     *   The walk that finds every bookmark, and the package each page is wrapped in, are made
     *   once and shared by every page. No caller holds the range, so nothing can change it
     *   under the pages.
     */
    static bookmarkedPagesOf(bodyPackageOoxml: string): BookmarkedPages {
        const documentRead = new OoxmlRange(bodyPackageOoxml);
        const markerIndex = documentRead.bookmarkMarkerIndex();
        /**
         * NULL
         *   before the first page is cut: a run whose cards all fall back to Word never
         *   serializes the package.
         */
        let packageAroundBodyContent: PackageAroundBodyContent | null = null;
        /**
         * NULL
         *   before the first table page is cut: a run with no table card never walks the
         *   read's tables or names its paragraphs' bookmarks.
         */
        let tablePageCutIndex: TablePageCutIndex | null = null;
        const pageOf = (bookmarkNames: string[]): BookmarkedPage | null =>
            documentRead.bookmarkedPage(bookmarkNames, markerIndex, () => (packageAroundBodyContent ??= documentRead.packageAroundBodyContent()));
        const tablePageOf = (listedBookmarkNames: string[]): BookmarkedTablePage => documentRead.bookmarkedTablePage(listedBookmarkNames, (tablePageCutIndex ??= documentRead.tablePageCutIndex()));
        /**
         * CASES
         *   - none of the bookmarks is in the read           -> null
         *   - their paragraphs are body paragraphs           -> the span's page
         *   - a paragraph sits outside the body (a cell, a
         *     block content control)                         -> the cells' page of every listed bookmark, or
         *                                                       readFromWord where the read leaves it to Word
         */
        const applyPageOf = (bookmarkNames: string[], listedBookmarkNames: string[]): BookmarkedApplyPage | null => {
            const spanPage = pageOf(bookmarkNames);
            return spanPage === null || spanPage.kind === "page" ? spanPage : tablePageOf(listedBookmarkNames);
        };
        /**
         * CASES, for one bookmark
         *   - not in the read                       -> left out: there is nothing of it to re-lay
         *   - in the read, settled in one paragraph -> left out
         *   - in the read, not settled              -> kept
         */
        const unsettledBookmarkNamesOf = (bookmarkNames: string[]): string[] =>
            bookmarkNames.filter((bookmarkName) => markerIndex.markersByBookmarkName.has(bookmarkName) && !documentRead.bookmarksSettledInOneParagraph([bookmarkName], markerIndex));
        return {
            pageOf,
            tablePageOf,
            applyPageOf,
            holdsAnyOf: (bookmarkNames) => documentRead.holdsAnyBookmarkOnAParagraph(bookmarkNames, markerIndex),
            unsettledBookmarkNamesOf,
        };
    }

    /**
     * Whether any of these bookmarks touches a paragraph of the read, a table cell's
     * paragraph included (`bodyParagraphs`), the test `bookmarkedPage` cuts no page on.
     *
     * CASES, for one bookmark
     *   - no start in the read                    -> no
     *   - its start after the last body paragraph -> no: its range touches none (`paragraphSpanOf`)
     *   - otherwise                               -> yes
     */
    private holdsAnyBookmarkOnAParagraph(bookmarkNames: string[], markerIndex: BookmarkMarkerIndex): boolean {
        return bookmarkNames.some((bookmarkName) => {
            const markers = markerIndex.markersByBookmarkName.get(bookmarkName);
            return markers !== undefined && this.paragraphSpanOf(markers) !== null;
        });
    }

    /**
     * The paragraph each bookmark of the read names (`paragraphElementsByBookmarkName`, the
     * reading a single paragraph's read is asked the same question with) and the read's
     * tables (`TableStructure`, the reading `documentParagraphs` places cells with), each
     * walked once for every table page of the read.
     */
    private tablePageCutIndex(): TablePageCutIndex {
        return { paragraphElementByBookmarkName: this.paragraphElementsByBookmarkName(), tableStructure: new TableStructure(OoxmlRange.bodyOf(this.rangeDocument)) };
    }

    /**
     * The page of a table group, cut out of the read: the paragraph each listed bookmark
     * names, alone, in the document's order, each once, in one package, and the row of
     * each listed cell. It is the page the add-in builds from Word's read of each listed
     * paragraph on its own (`tablePageReadOneByOne`), made without asking Word.
     *
     * WHY each paragraph alone, never the span between them
     *   Word exports a range that crosses table rows as its first row alone (live probe,
     *   Word for Mac 16.114, 2026-10-16), so the fold composes a table card from the
     *   paragraphs of a page read one by one (R35). The cut keeps that page's shape, so
     *   the fold and the cell writer read it as they read Word's.
     *
     * WHY the rows come from the markup
     *   Word Online's export of one cell's paragraph carries no row around it, so the
     *   add-in asks Office.js where each cell sits (`cellRowKeysOf`). The whole-document
     *   read holds every table, row and cell, so the read places them itself.
     *
     * CASES
     *   - a listed bookmark names no paragraph of the read   -> readFromWord: Word finds a bookmark its
     *                                                           export left out, or names the cell the user
     *                                                           deleted (`bookmarkNamesNotFoundInDocument`)
     *   - no listed paragraph sits in a table cell (a block
     *     content control's paragraphs)                      -> readFromWord: Word reads them as one span
     *   - otherwise                                          -> the page and every listed cell's row
     *
     * EXAMPLE
     *   the question table first in the body; listed [_row2_ask, _row1_ask, _row1_ask_twin],
     *   _row1_ask_twin on _row1_ask's paragraph
     *   -> page   <w:p _row1_ask _row1_ask_twin/><w:p _row2_ask/>
     *      rows   { _row2_ask: "1:2", _row1_ask: "1:1", _row1_ask_twin: "1:1" }
     */
    private bookmarkedTablePage(listedBookmarkNames: string[], tablePageCutIndex: TablePageCutIndex): BookmarkedTablePage {
        const { paragraphElementByBookmarkName, tableStructure } = tablePageCutIndex;
        const listedParagraphs = listedBookmarkNames.flatMap((bookmarkName): ListedBookmarkParagraph[] => {
            const namedParagraph = paragraphElementByBookmarkName.get(bookmarkName);
            /** A bookmark the read holds no paragraph for is left out here, and the count below sees it missing. */
            return namedParagraph === undefined ? [] : [{ bookmarkName, namedParagraph }];
        });
        /**
         * WHEN a listed bookmark names no paragraph of the read
         *   CONSEQUENCE
         *     the whole page is asked of Word, which reads every bookmark it still finds
         *     and names the others gone.
         */
        if (listedParagraphs.length < listedBookmarkNames.length) return { kind: "readFromWord" };
        const cellRowKeyByBookmarkName = new Map<string, string>();
        for (const { bookmarkName, namedParagraph } of listedParagraphs) {
            const cellPath = tableStructure.cellPathOf(namedParagraph);
            /**
             * The path lists the cells around the paragraph outermost first, so its last entry
             * is the cell the paragraph is written in, and a nested table's paragraph names its
             * inner table's row.
             *
             * EXAMPLE
             *   path [{ tableNumber 1, rowNumber 3 }, { tableNumber 2, rowNumber 1 }] -> length 2 - 1 = index 1 -> "2:1"
             *   path []                                                              -> index -1 -> undefined: outside every table
             */
            const innermostCell = cellPath[cellPath.length - 1];
            /** WHEN the paragraph sits in a cell CONSEQUENCE its row is named; a body paragraph of the group names none. */
            if (innermostCell !== undefined) cellRowKeyByBookmarkName.set(bookmarkName, `${innermostCell.tableNumber}:${innermostCell.rowNumber}`);
        }
        /**
         * WHEN no listed paragraph sits in a table cell: the paragraphs are a block content control's
         *   CONSEQUENCE
         *     Word reads them as one span, the page `pageOf` leaves to Word.
         */
        if (cellRowKeyByBookmarkName.size === 0) return { kind: "readFromWord" };
        const paragraphsNamed = new Set(listedParagraphs.map((listedParagraph) => listedParagraph.namedParagraph));
        const xmlSerializer = XmlHost.xmlSerializer();
        /** The read's paragraphs in document order, kept where a listed bookmark names one: each goes on the page once. */
        const pageParagraphXmls = this.paragraphElements
            .filter((readParagraph) => paragraphsNamed.has(readParagraph))
            .map((pageParagraph) => {
                const paragraphAlone = new OoxmlRange(OoxmlRange.packageAround(xmlSerializer.serializeToString(pageParagraph)));
                /**
                 * A bookmark whose start sits outside its paragraph (at cell level, before the
                 * cell's paragraph) is not in the paragraph serialized alone, so it goes back on
                 * it, at index 0, the one paragraph of this package: as the add-in puts back a
                 * bookmark a paragraph's own read left out (`paragraphReadOfBookmark`). A bookmark
                 * the paragraph already carries stays as the read holds it.
                 *
                 * WHY each paragraph alone
                 *   Each one is put in its own package and written out on its own
                 *   (`paragraphXml`), as the page Word's reads are made into is, so a namespace
                 *   the serializer declared on one paragraph is never left to another.
                 */
                paragraphAlone.restoreDroppedBookmarks(new Map(listedParagraphs.filter((listedParagraph) => listedParagraph.namedParagraph === pageParagraph).map((listedParagraph) => [listedParagraph.bookmarkName, 0])));
                /** The package holds the one paragraph, at index 0. */
                return paragraphAlone.paragraphXml(0);
            });
        return { kind: "tablePage", packageOoxml: OoxmlRange.packageAround(pageParagraphXmls.join("")), cellRowKeyByBookmarkName };
    }

    /**
     * The body paragraphs each named bookmark's range touches, from the paragraph its start
     * is in to the paragraph its end is in (`paragraphSpanOf`), as indexes into
     * `documentParagraphs`.
     *
     * WHY
     *   `documentParagraphs` lists a bookmark only on the paragraph its start is in. A user
     *   who splits a bookmarked paragraph by pressing Enter leaves its end in the split-off
     *   half (R22, 2026-10-16: 7.4's "_reload_detail_frame" ending in "Supplier retains …"),
     *   and the landing audit reads every paragraph the bookmark still touches.
     *
     * NULL
     *   A name is left out of the map when the read holds no start for it, or its start sits
     *   after the last body paragraph.
     *
     * EXAMPLE
     *   start in paragraph 176, end in paragraph 177 -> { firstParagraphIndex: 176, lastParagraphIndex: 177 }
     */
    bookmarkParagraphSpans(bookmarkNames: string[]): Map<string, BookmarkParagraphSpan> {
        const { markersByBookmarkName } = this.bookmarkMarkerIndex();
        const spansByBookmarkName = new Map<string, BookmarkParagraphSpan>();
        for (const bookmarkName of bookmarkNames) {
            const markers = markersByBookmarkName.get(bookmarkName);
            /** A name the read holds no start for has no span, as `NULL` above says. */
            const span = markers === undefined ? null : this.paragraphSpanOf(markers);
            /** WHEN the bookmark touches a body paragraph of the read CONSEQUENCE its span is kept under its name. */
            if (span !== null) spansByBookmarkName.set(bookmarkName, span);
        }
        return spansByBookmarkName;
    }

    /**
     * Every bookmark marker of the read and where it sits among the body paragraphs, found
     * in one walk of the body in document order.
     *
     * CASES, for a marker
     *   - inside a body paragraph        -> that paragraph, and the text elements read before it there
     *   - at body level: between
     *     paragraphs, before a table, in
     *     a block content control's
     *     wrapper                        -> no enclosing paragraph; `paragraphsBegunBefore` is the
     *                                       index of the paragraph after it
     *   - inside an `mc:Fallback` copy   -> skipped, the copy `bodyParagraphs` skips
     *   - a second start with one name   -> skipped: Word resolves a name to its first bookmark
     *
     * EDGE CASE
     *   A paragraph inside a text box is not a body paragraph: its markers and text count
     *   toward the body paragraph that holds the text box.
     */
    private bookmarkMarkerIndex(): BookmarkMarkerIndex {
        const paragraphIndexByElement = new Map(this.paragraphElements.map((paragraph, paragraphIndex) => [paragraph, paragraphIndex]));
        const textElementCountByParagraph = this.paragraphElements.map(() => 0);
        const startsSeen: BookmarkStartSeen[] = [];
        const endPositionByBookmarkId = new Map<string, BookmarkMarkerPosition>();
        let paragraphsBegun = 0;
        const visit = (element: Element, enclosingParagraphIndex: number | null): void => {
            const elementName = elementNameWithoutPrefix(element);
            if (elementName === "Fallback") return;
            const ownParagraphIndex = paragraphIndexByElement.get(element);
            /**
             * WHEN the element is a body paragraph
             *   CONSEQUENCE
             *     it has begun: `ownParagraphIndex + 1` paragraphs have, e.g. paragraph 3 -> 4 begun
             *     (0, 1, 2 and 3), so a body-level marker after it names paragraph 4 as the next.
             */
            if (ownParagraphIndex !== undefined) paragraphsBegun = ownParagraphIndex + 1;
            const paragraphIndexHere = ownParagraphIndex ?? enclosingParagraphIndex;
            if (elementName === "bookmarkStart" || elementName === "bookmarkEnd") {
                const position: BookmarkMarkerPosition = {
                    enclosingParagraphIndex: paragraphIndexHere,
                    paragraphsBegunBefore: paragraphsBegun,
                    /**
                     * CASES
                     *   - inside a paragraph -> the text elements of that paragraph met so far
                     *   - at body level      -> 0
                     */
                    textElementsBefore: paragraphIndexHere === null ? 0 : textElementCountByParagraph[paragraphIndexHere],
                };
                const bookmarkId = TableStructure.attributeOf(element, "id") ?? "";
                /**
                 * CASES
                 *   - a start            -> kept with its name, paired with its end once the walk is done
                 *   - an end, first seen -> kept by id
                 *   - an end seen again  -> ignored: ids are unique, a repeat is malformed markup
                 */
                if (elementName === "bookmarkStart") startsSeen.push({ bookmarkName: TableStructure.attributeOf(element, "name") ?? "", bookmarkId, position });
                else if (!endPositionByBookmarkId.has(bookmarkId)) endPositionByBookmarkId.set(bookmarkId, position);
                return;
            }
            /**
             * WHEN the element holds paragraph characters inside a paragraph (`w:t` or `w:delText`)
             *   CONSEQUENCE
             *     counted, struck text included, as `paragraphBookmarkNamesByOwner` counts it.
             */
            if (TEXT_TAGS.has(elementName) && paragraphIndexHere !== null) textElementCountByParagraph[paragraphIndexHere] += 1;
            for (const child of Array.from(element.childNodes)) {
                if (child.nodeType === 1) visit(child as Element, paragraphIndexHere);
            }
        };
        visit(OoxmlRange.bodyOf(this.rangeDocument), null);
        const markersByBookmarkName = new Map<string, BookmarkMarkerPair>();
        for (const start of startsSeen) {
            /**
             * WHEN the start has no name, or an earlier start already carries it
             *   CONSEQUENCE
             *     skipped: the first is the bookmark Word resolves the name to.
             */
            if (start.bookmarkName === "" || markersByBookmarkName.has(start.bookmarkName)) continue;
            markersByBookmarkName.set(start.bookmarkName, { start: start.position, end: endPositionByBookmarkId.get(start.bookmarkId) ?? null });
        }
        return { markersByBookmarkName, textElementCountByParagraph };
    }

    /**
     * The body paragraphs a bookmark's range touches: from the paragraph its start is in, or
     * the one after a body-level start, to the paragraph its end is in, or the one before a
     * body-level end. That is the range `readRange` widens to whole paragraphs.
     *
     * NULL
     *   the start sits after the last body paragraph, so the range touches none.
     *
     * EXAMPLE
     *   <w:p>A</w:p><w:bookmarkStart/><w:p>B</w:p><w:p>C<w:bookmarkEnd/></w:p>
     *   start at body level, 1 paragraph begun before it -> first = 1 (B)
     *   end inside C                                     -> last  = 2 (C)
     */
    private paragraphSpanOf(markers: BookmarkMarkerPair): BookmarkParagraphSpan | null {
        const firstParagraphIndex = markers.start.enclosingParagraphIndex ?? markers.start.paragraphsBegunBefore;
        if (firstParagraphIndex >= this.paragraphElements.length) return null;
        /**
         * CASES
         *   - no end in the read     -> the start's paragraph alone
         *   - end inside a paragraph -> that paragraph
         *   - end at body level      -> the paragraph before it: `paragraphsBegunBefore - 1`,
         *                               e.g. 3 paragraphs begun before the end -> index 2, the third
         */
        const endParagraphIndex = markers.end === null ? firstParagraphIndex : (markers.end.enclosingParagraphIndex ?? markers.end.paragraphsBegunBefore - 1);
        /**
         * WHEN the end lands before the start's paragraph: an empty bookmark at body level between two paragraphs
         *   CONSEQUENCE
         *     the span is the start's paragraph alone.
         */
        return { firstParagraphIndex, lastParagraphIndex: Math.max(firstParagraphIndex, endParagraphIndex) };
    }

    /**
     * The page Word's export of these bookmarks' paragraphs gives (`readRange`: their range
     * widened to whole paragraphs), cut out of the read.
     *
     * CASES
     *   - none of the bookmarks is in the read          -> null, as Word's read returns
     *   - a paragraph of the page is not a direct child
     *     of the body: a table cell, a block content
     *     control                                       -> readFromWord: Word exports those in its own shape,
     *                                                      and a table group's page is cut one paragraph at
     *                                                      a time instead (`bookmarkedTablePage`)
     *   - otherwise                                     -> the paragraphs from the first one a bookmark
     *                                                      touches to the last, in the read's package
     *
     * WHAT the page holds, as Word's export does (compared on all 274 bookmarks of a QA document)
     *   - a start of one of these bookmarks that Word wrote at body level just before the first
     *     paragraph (a content control's paragraph)
     *   - everything at body level between the first and the last paragraph
     *   - the body's section properties
     *   - the read's other parts (styles, numbering, comments, theme), so styles and numbering
     *     resolve as they do on Word's page
     *   - the media parts its paragraphs, the document's background, a header or a footer use
     *
     * WHAT it leaves out
     *   the media parts nothing of that page uses, and the embedded fonts (`PackageBinaryParts`)
     *   and the empty paragraph Word ends its own export with, which every read of Word leaves
     *   out too (`readOfWordExport`): every reader of the page gets the
     *   document's paragraphs alone, and Apply puts it back as it writes
     *   (`withWordsClosingParagraph`).
     *
     * EDGE CASE
     *   Word renumbers bookmark, revision and comment ids from 0 in each export and stamps
     *   fresh rsids; the cut keeps the document's. Nothing reads them as positions: a revert
     *   names a change by its place in the paragraph (`c1`, `c2`), never by Word's id.
     */
    private bookmarkedPage(bookmarkNames: string[], markerIndex: BookmarkMarkerIndex, packageAroundBodyContent: () => PackageAroundBodyContent): BookmarkedPage | null {
        const spans = bookmarkNames
            .map((bookmarkName) => markerIndex.markersByBookmarkName.get(bookmarkName))
            .filter((markers): markers is BookmarkMarkerPair => markers !== undefined)
            .map((markers) => this.paragraphSpanOf(markers))
            .filter((span): span is BookmarkParagraphSpan => span !== null);
        if (spans.length === 0) return null;
        const firstParagraphIndex = Math.min(...spans.map((span) => span.firstParagraphIndex));
        const lastParagraphIndex = Math.max(...spans.map((span) => span.lastParagraphIndex));
        /**
         * EXAMPLE
         *   first 4, last 6 -> slice(4, 7): `lastParagraphIndex + 1` because slice stops before its end,
         *   so paragraphs 4, 5 and 6 are taken
         */
        const pageParagraphs = this.paragraphElements.slice(firstParagraphIndex, lastParagraphIndex + 1);
        const bodyElement = OoxmlRange.bodyOf(this.rangeDocument);
        if (pageParagraphs.some((paragraph) => paragraph.parentNode !== bodyElement)) return { kind: "readFromWord" };
        const xmlSerializer = XmlHost.xmlSerializer();
        const pageElements = [...OoxmlRange.bodyLevelStartsJustBefore(pageParagraphs[0], new Set(bookmarkNames))];
        /**
         * EXAMPLE
         *   pageParagraphs.length = 3 -> `length - 1` = 2, the page's last paragraph, where the walk stops
         */
        const lastPageParagraph = pageParagraphs[pageParagraphs.length - 1];
        for (let bodyChild: ChildNode | null = pageParagraphs[0]; bodyChild !== null; bodyChild = bodyChild.nextSibling) {
            if (bodyChild.nodeType === 1) pageElements.push(bodyChild as Element);
            if (bodyChild === lastPageParagraph) break;
        }
        const sectionProperties = TableStructure.childNamed(bodyElement, "sectPr");
        /**
         * CASES
         *   - the body has section properties -> they close the page, as they close Word's export
         *   - it has none                     -> the page ends with its last paragraph
         */
        const sectionPropertiesXml = sectionProperties === null ? "" : xmlSerializer.serializeToString(sectionProperties);
        const pageBodyContent = `${pageElements.map((pageElement) => xmlSerializer.serializeToString(pageElement)).join("")}${sectionPropertiesXml}`;
        return { kind: "page", packageOoxml: packageAroundBodyContent().packageAroundPageBodyContent(pageBodyContent) };
    }

    /**
     * The starts of these bookmarks Word wrote at body level directly before `paragraph`,
     * in document order. Word writes a bookmark that begins at a paragraph's first
     * character this way, and keeps that start in its export of the paragraph.
     *
     * EXAMPLE
     *   <w:bookmarkEnd w:id="7"/><w:bookmarkStart w:name="_pay"/><w:p>…</w:p>
     *   bookmarkNames ["_pay"] -> [the start of _pay]; the end of bookmark 7 belongs to another range
     */
    private static bodyLevelStartsJustBefore(paragraph: Element, bookmarkNames: Set<string>): Element[] {
        const startsBefore: Element[] = [];
        for (let sibling = paragraph.previousSibling; sibling !== null; sibling = sibling.previousSibling) {
            /**
             * CASES
             *   - whitespace between elements      -> passed over
             *   - a bookmark marker                -> read, and the walk goes on
             *   - anything else (a paragraph, a
             *     table)                           -> the run of markers before the paragraph ends here
             */
            if (sibling.nodeType !== 1) continue;
            const siblingElement = sibling as Element;
            const siblingName = elementNameWithoutPrefix(siblingElement);
            if (siblingName !== "bookmarkStart" && siblingName !== "bookmarkEnd") break;
            if (siblingName === "bookmarkStart" && bookmarkNames.has(TableStructure.attributeOf(siblingElement, "name") ?? "")) startsBefore.unshift(siblingElement);
        }
        return startsBefore;
    }

    /**
     * The read's package as text with its body content cut out: what comes before the body's
     * first child and what comes after its last, so each page is wrapped in the same parts,
     * less the media parts the page does not use (`PackageMediaForPages`).
     *
     * FALLBACK
     *   A read whose body is not written `<w:body>…</w:body>` (another prefix) cannot be cut
     *   at the tag: its pages go in a minimal package (`packageAround`) without its other parts.
     */
    private packageAroundBodyContent(): PackageAroundBodyContent {
        const serializedPackage = XmlHost.xmlSerializer().serializeToString(this.rangeDocument);
        const bodyOpenTagStart = serializedPackage.indexOf("<w:body");
        /**
         * EXAMPLE
         *   "…<w:body><w:p>"  the ">" closing the body's open tag at 40 -> `40 + 1` = 41, where its content begins
         */
        const bodyContentStart = bodyOpenTagStart < 0 ? -1 : serializedPackage.indexOf(">", bodyOpenTagStart) + 1;
        const bodyContentEnd = serializedPackage.lastIndexOf("</w:body>");
        /**
         * WHEN either tag is missing, or the body was written self-closed
         *   CONSEQUENCE
         *     the minimal package, through the FALLBACK above.
         */
        if (bodyContentStart <= 0 || bodyContentEnd < bodyContentStart) {
            const [beforeBodyContent, afterBodyContent] = OoxmlRange.packageAround(PAGE_BODY_CONTENT_MARKER).split(PAGE_BODY_CONTENT_MARKER);
            return { packageAroundPageBodyContent: (pageBodyContent) => `${beforeBodyContent}${pageBodyContent}${afterBodyContent}` };
        }
        const packageWithBodyContentMarker = `${serializedPackage.slice(0, bodyContentStart)}${PAGE_BODY_CONTENT_MARKER}${serializedPackage.slice(bodyContentEnd)}`;
        const mediaForPages = PackageMediaForPages.separatedFrom(packageWithBodyContentMarker);
        return { packageAroundPageBodyContent: (pageBodyContent) => mediaForPages.packageForPageBodyContent(pageBodyContent) };
    }

    /**
     * Whether each of these bookmarks the read holds starts and ends inside one paragraph:
     * the shape Apply writes over without Word settling the bookmark's edges first
     * (`DocumentBookmarks.repairEdges`, which a card's first reconcile runs).
     *
     * CASES, for one bookmark
     *   - not in the read                                -> skipped: a card partly gone reads its others
     *   - no end in the read, or the end outside every
     *     paragraph                                      -> not settled: Word grew it over the paragraph mark
     *   - start and end in different paragraphs          -> not settled: stretched across a paragraph break,
     *                                                       which `repairEdges` trims
     *   - start inside its paragraph after all its text  -> not settled: the start is the tail Word leaves
     *                                                       in a paragraph when Enter is pressed before it
     *   - start inside its paragraph with text after it,
     *     or that paragraph holds no text                -> settled
     *   - start at body level just before the paragraph
     *     the end is in                                  -> settled: how Word writes a bookmark that begins
     *                                                       at a paragraph's first character
     *
     * RETURNS
     *   false when none of the bookmarks is in the read: a card whose bookmarks are all gone is
     *   found again by recovery.
     */
    private bookmarksSettledInOneParagraph(bookmarkNames: string[], markerIndex: BookmarkMarkerIndex): boolean {
        const presentMarkers = bookmarkNames
            .map((bookmarkName) => markerIndex.markersByBookmarkName.get(bookmarkName))
            .filter((markers): markers is BookmarkMarkerPair => markers !== undefined);
        if (presentMarkers.length === 0) return false;
        return presentMarkers.every((markers) => {
            const endParagraphIndex = markers.end?.enclosingParagraphIndex ?? null;
            if (endParagraphIndex === null) return false;
            const { start } = markers;
            if (start.enclosingParagraphIndex === null) return start.paragraphsBegunBefore === endParagraphIndex;
            if (start.enclosingParagraphIndex !== endParagraphIndex) return false;
            const textElementsInParagraph = markerIndex.textElementCountByParagraph[endParagraphIndex];
            return textElementsInParagraph === 0 || textElementsInParagraph > start.textElementsBefore;
        });
    }

    /**
     * For every body paragraph, in order, whether the revision stamped `author` and
     * `stampedAt` inserted it whole.
     *
     * CASES
     *   - a `w:ins` inside it carries another author or another stamp       -> false: a paragraph an earlier redline inserted, the same
     *                                                                           user's included, that the document still holds as tracked
     *   - its paragraph mark carries `w:pPr/w:rPr/w:ins` by the revision    -> true: how `insertParagraph` marks a new paragraph and
     *                                                                           `TableRowXml` every cell of an added row, an empty cell included
     *   - it holds text and every piece was inserted by the author          -> true: a package that marks runs only
     *   - it holds no text and no such paragraph mark                       -> false: an empty paragraph the document already held
     *   - otherwise                                                         -> false
     *
     * WHY the stamp as well as the author
     *   A chat card is written under the user's own name, so a row an earlier card
     *   added, applied and not yet accepted, carries the same author as the card
     *   being previewed. Every revision of one redline shares its `w:date`
     *   (`RevisionConfig.stampedAt`), so the stamp tells the two cards apart.
     *
     * WHY
     *   The edit layer lays a user's text for a new paragraph or an added cell only over
     *   a paragraph the proposal itself inserted, so a paragraph the document already
     *   held that reads the same is never taken for it.
     */
    paragraphsInsertedBy(author: string, stampedAt: string): boolean[] {
        return this.paragraphElements.map((paragraph) => {
            const insertionContainers = Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "ins"));
            const stampedByRevision = (insertionContainer: Element): boolean =>
                Revision.sameAuthor(TableStructure.attributeOf(insertionContainer, "author"), author) && TableStructure.attributeOf(insertionContainer, "date") === stampedAt;
            if (insertionContainers.length === 0 || !insertionContainers.every(stampedByRevision)) return false;
            const paragraphProperties = TableStructure.childNamed(paragraph, "pPr");
            const markRunProperties = paragraphProperties === null ? null : TableStructure.childNamed(paragraphProperties, "rPr");
            const markInsertion = markRunProperties === null ? null : TableStructure.childNamed(markRunProperties, "ins");
            if (markInsertion !== null) return true;
            const textPieces = new ParagraphModel(paragraph).pieces;
            return textPieces.length > 0 && textPieces.every((textPiece) => Revision.sameAuthor(textPiece.insertedBy, author));
        });
    }

    paragraphIds(): ParagraphIds[] {
        return this.paragraphElements.map((paragraph) => OoxmlRange.idsOf(paragraph));
    }

    /**
     * The body paragraph each bookmark name in this document names — a
     * paragraph's own `_dioptra_p_*` names and every other bookmark it answers
     * to alike, the same names `documentParagraphs` reports it under.
     *
     * WHY a caller wants this
     *   A name survives a write; a position does not. Between the read that
     *   handed a caller a name and the write that uses it the document gains
     *   and loses paragraphs — a proposal that inserts one moves every
     *   paragraph after it — so resolving by the index of the read would name
     *   the paragraph next door.
     */
    paragraphElementsByBookmarkName(): Map<string, Element> {
        const ownedParagraphBookmarkNamesByParagraph = OoxmlBookmarks.paragraphBookmarkNamesByOwner(OoxmlRange.bodyOf(this.rangeDocument), this.paragraphElements);
        const paragraphElementByBookmarkName = new Map<string, Element>();
        for (const paragraph of this.paragraphElements) {
            const ownedParagraphBookmarkNames = ownedParagraphBookmarkNamesByParagraph.get(paragraph) ?? [];
            for (const bookmarkName of OoxmlRange.bookmarkNamesOf(paragraph, ownedParagraphBookmarkNames)) {
                const anEarlierParagraphAlreadyAnswersToThisName = paragraphElementByBookmarkName.has(bookmarkName);
                /**
                 * WHEN a bookmark spans several paragraphs, so more than one answers to its name
                 *   CONSEQUENCE
                 *     keep the first: it holds the bookmark's start, and it is the paragraph a reader lists the name under.
                 */
                if (anEarlierParagraphAlreadyAnswersToThisName) continue;
                paragraphElementByBookmarkName.set(bookmarkName, paragraph);
            }
        }
        return paragraphElementByBookmarkName;
    }

    /**
     * One trimmed span per paragraph: from the bookmarks when present, else the
     * whole paragraph. `neighbourTexts` are the texts of the paragraphs the
     * tracker remembers around the redline; when the bookmark has swallowed one
     * of them the span is shrunk off it.
     */
    bookmarkSpans({ bookmarkNames, neighbourTexts }: { bookmarkNames: string[]; neighbourTexts: string[] }): TrimmedBookmarkSpan[] {
        const rangeBookmarks = new OoxmlBookmarks(OoxmlRange.bodyOf(this.rangeDocument), this.paragraphElements, bookmarkNames);
        return this.paragraphElements.map((paragraph) => {
            const paragraphModel = new ParagraphModel(paragraph);
            const bookmarkAcceptedTextSpan = (bookmarkNames.length > 0 ? rangeBookmarks.spanWithin(paragraph, paragraphModel) : null) ?? { startOffset: 0, endOffset: paragraphModel.text.length };
            return trimSpanByNeighbours({ paragraphText: paragraphModel.text, span: bookmarkAcceptedTextSpan, neighbourTexts });
        });
        /**
         * Shrinks the span off any neighbour text at either end and names the words
         * the bookmark edges should move to. A span that would vanish is left alone.
         *
         * The case: Word grows a bookmark over the previous paragraph when Enter is
         * pressed at its start, so the range read for the redline begins with the
         * tail of the neighbour ("...rights, whether registered or not.") followed by
         * the redline's own paragraph. Most of the neighbour must match, from the
         * start of the span, for words to be trimmed; an edited last word of the
         * neighbour still counts, which is what the slack and the trailing replace
         * allowance are for.
         *
         * WHY the words are the redline diff's units (`WordTokenizer.diffUnits`)
         *   Japanese, Chinese and Thai put no spaces between words, so cut at
         *   whitespace a neighbour in them was one word, under the two words a match
         *   needs, and was never trimmed: the span kept the whole merged paragraph.
         *
         *   EXAMPLE
         *     neighbour "本契約は、注文書に記載された供給者と顧客との間で締結される。"
         *     span      "本契約は、注文書に記載された供給者と顧客との間で締結された。本契約は、両当事者が…"
         *     in units the neighbour's 23 words open the span, its last ones edited
         *     -> the span keeps "本契約は、両当事者が…" and the start edge moves after "。"
         */
        function trimSpanByNeighbours({ paragraphText, span, neighbourTexts }: { paragraphText: string; span: AcceptedTextSpan; neighbourTexts: string[] }): TrimmedBookmarkSpan {
            const MIN_MATCHED_WORDS = 2;
            const MATCH_RATIO = 0.75;
            const SLACK_RATIO = 0.3;
            const SLACK_WORDS = 2;

            /**
             * How many leading words of `liveWords` are really the tail of a neighbouring paragraph
             * the bookmark swallowed, and how many of those the neighbour does not hold.
             */
            const swallowedNeighbourPrefix = (liveWords: string[], neighbourWords: string[]): SwallowedNeighbourWords => {
                const neighbourWordsAreEmpty = neighbourWords.length === 0;
                const liveWordsAreEmpty = liveWords.length === 0;
                const wordsMissingForPrefixMatch = neighbourWordsAreEmpty || liveWordsAreEmpty;
                // An empty neighbour cannot have been swallowed, and an empty read has nothing to trim.
                if (wordsMissingForPrefixMatch) return NOTHING_SWALLOWED;
                const extraCandidateWords = Math.ceil(neighbourWords.length * SLACK_RATIO) + SLACK_WORDS;
                /**
                 * WHY
                 *   Compare only the live prefix that could still be swallowed neighbour text.
                 *
                 * EXAMPLE
                 *   neighbourWords.length = 5
                 *   slack                 = 3
                 *   slice(0, 8)           -> first 8 live words
                 */
                const candidateLivePrefixWords = liveWords.slice(0, neighbourWords.length + extraCandidateWords);
                const diffOps = wordDiff(candidateLivePrefixWords, neighbourWords);
                let equalWordCount = 0;
                let matchedBoundary = 0;
                let lastEqualIndex = -1;
                let firstEqual: WordDiffHunk | null = null;
                for (const [index, op] of diffOps.entries()) {
                    /**
                     * WHEN the diff op is not equal
                     *   CONSEQUENCE
                     *     Skip it. Only unchanged runs prove neighbour-text overlap.
                     */
                    if (op.tag !== "equal") continue;
                    equalWordCount += op.beforeEnd - op.beforeStart;
                    matchedBoundary = op.beforeEnd;
                    lastEqualIndex = index;
                    /**
                     * WHEN this is the first equal run seen
                     *   CONSEQUENCE
                     *     Remember where matching began for the later boundary-position guard.
                     */
                    if (firstEqual === null) firstEqual = op;
                }
                const matchedWordsNeededByRatio = Math.ceil(neighbourWords.length * MATCH_RATIO);
                const meetsNeighbourMatchThreshold = equalWordCount >= Math.max(MIN_MATCHED_WORDS, Math.max(MIN_MATCHED_WORDS, matchedWordsNeededByRatio));
                /**
                 * CASES
                 *   - matched words are too few      -> nothing swallowed
                 *   - no equal run exists            -> nothing swallowed
                 *   - first match starts too late    -> nothing swallowed
                 *
                 * RETURNS
                 *   0 matched leading words: no swallowed-neighbour prefix accepted.
                 */
                if (!meetsNeighbourMatchThreshold || firstEqual === null || firstEqual.beforeStart > firstEqual.afterStart + 1) return NOTHING_SWALLOWED;
                /**
                 * WHY
                 *   Inspect the diff op immediately after the last equal run.
                 *
                 * EXAMPLE
                 *   lastEqualIndex = 4
                 *   read ops[5]    -> trailing op
                 */
                const trailingDiffOp = diffOps[lastEqualIndex + 1];
                /**
                 * CASES
                 *   - no op follows the last equal run -> no: the texts end together
                 *   - one does                         -> yes when it swaps words rather than only adding or removing
                 */
                const trailingDiffOpIsReplace = (): boolean => {
                    if (!trailingDiffOp) return false;
                    return trailingDiffOp.tag === "replace";
                };
                /**
                 * WHEN the op after the last equal run is replace
                 *   CONSEQUENCE
                 *     Extend the boundary by the shorter replaced tail on the two sides.
                 */
                if (trailingDiffOpIsReplace()) matchedBoundary += Math.min(trailingDiffOp.afterEnd - trailingDiffOp.afterStart, trailingDiffOp.beforeEnd - trailingDiffOp.beforeStart);
                const swallowedWordCount = Math.min(matchedBoundary, liveWords.length);
                /**
                 * EXAMPLE
                 *   the neighbour's 24 words matched one for one          -> swallowed 24, 24 equal -> 0 not in the neighbour
                 *   "本" "契約" matched at the span's start, then the
                 *   redline's own 7 words and 2 more skipped, then 22 more -> swallowed 33, 24 equal -> 9 not in the neighbour
                 */
                return { swallowedWordCount, wordsNotInNeighbourCount: swallowedWordCount - equalWordCount };
            };
            const swallowedNeighbourSuffix = (liveWords: string[], neighbourWords: string[]): SwallowedNeighbourWords =>
                swallowedNeighbourPrefix([...liveWords].reverse(), [...neighbourWords].reverse());
            /**
             * How many times the word's text occurs in the span before the word, counted
             * as Word's search counts the hits the bookmark edge is moved by: every place
             * the text stands, inside a longer word too, each hit resuming after the last.
             *
             * WHY not count the equal words before it
             *   A Japanese, Chinese or Thai word of one or two characters is often part of
             *   a longer word as well. Counted as words, the edge named a hit Word's search
             *   reaches earlier, inside that longer word, and the edge moved there.
             *
             * EXAMPLE
             *   span "準拠法は日本法とする。本契約に基づく…", the word "本" at 11
             *   indexOf("本") -> 5, inside 日本; from 5 + 1 -> 11, the word itself, not before it
             *   -> 1: Word's second hit for "本" is this word, where counting words said 0
             */
            const occurrenceIndexOf = (wordIndex: number): number => {
                const word = spanWords[wordIndex];
                let occurrencesBeforeWord = 0;
                let occurrenceStart = spanText.indexOf(word.text);
                /**
                 * WHEN another occurrence starts before the word itself
                 *   CONSEQUENCE
                 *     count it and look again from the character after its end
                 *
                 * EXAMPLE
                 *   occurrence of "本" at 5, length 1 -> next search starts at 5 + 1 = 6
                 */
                while (occurrenceStart !== -1 && occurrenceStart < word.startOffset) {
                    occurrencesBeforeWord += 1;
                    occurrenceStart = spanText.indexOf(word.text, occurrenceStart + word.text.length);
                }
                return occurrencesBeforeWord;
            };

            /**
             * WHY
             *   Take exactly the accepted-text segment covered by the bookmark span.
             *
             * EXAMPLE
             *   span.startOffset = 5
             *   span.endOffset   = 12
             *   slice(5, 12)     -> characters 5 through 11
             */
            const spanText = paragraphText.slice(span.startOffset, span.endOffset);
            const spanWords = WordTokenizer.diffUnits(spanText);
            const spanWordKeys = spanWords.map((word) => WordTokenizer.comparisonKey(word.text));
            let swallowedAtStart = NOTHING_SWALLOWED;
            let swallowedAtEnd = NOTHING_SWALLOWED;
            for (const neighbourText of neighbourTexts) {
                const neighbourWordKeys = WordTokenizer.diffUnits(neighbourText).map((word) => WordTokenizer.comparisonKey(word.text));
                const swallowedPrefix = swallowedNeighbourPrefix(spanWordKeys, neighbourWordKeys);
                const swallowedSuffix = swallowedNeighbourSuffix(spanWordKeys, neighbourWordKeys);
                /**
                 * CASES, per side
                 *   - this neighbour's match takes more words than the best so far -> it becomes the side's match
                 *   - otherwise                                                     -> the side keeps its match
                 */
                if (swallowedPrefix.swallowedWordCount > swallowedAtStart.swallowedWordCount) swallowedAtStart = swallowedPrefix;
                if (swallowedSuffix.swallowedWordCount > swallowedAtEnd.swallowedWordCount) swallowedAtEnd = swallowedSuffix;
            }
            const neighbourWordsToTrim = neighbourWordsToTrimFrom({ swallowedAtStart, swallowedAtEnd, spanWordCount: spanWords.length });
            /**
             * WHEN no trim keeps a word of the redline's own: the matches cover the whole span and
             *   neither can be told to be the neighbour's alone
             *   CONSEQUENCE
             *     Keep the original span and move neither bookmark edge.
             */
            if (neighbourWordsToTrim === null) return { span, startAfterWord: null, endBeforeWord: null };
            const { leadingNeighbourWords, trailingNeighbourWords } = neighbourWordsToTrim;
            /**
             * WHY
             *   Name the last swallowed leading word, so the start bookmark can move after it.
             *
             * EXAMPLE
             *   leadingNeighbourWords = 3
             *   index                 = 3 - 1 = 2
             *   spanWords[2]          -> third word, last one trimmed from the front
             */
            const trimmedLeadingEdgeWord = leadingNeighbourWords > 0
                ? { text: spanWords[leadingNeighbourWords - 1].text, occurrenceIndex: occurrenceIndexOf(leadingNeighbourWords - 1) }
                : null;
            const firstTrimmedTrailingWordIndex = spanWords.length - trailingNeighbourWords;
            /**
             * WHEN trailing neighbour words were trimmed
             *   CONSEQUENCE
             *     Name the first trimmed trailing word, so the end bookmark can move before it.
             */
            const trimmedTrailingEdgeWord = trailingNeighbourWords > 0
                ? { text: spanWords[firstTrimmedTrailingWordIndex].text, occurrenceIndex: occurrenceIndexOf(firstTrimmedTrailingWordIndex) }
                : null;
            return {
                span: {
                    startOffset: leadingNeighbourWords > 0 ? span.startOffset + spanWords[leadingNeighbourWords].startOffset : span.startOffset,
                    /**
                     * WHY
                     *   End the kept span at the last kept word, just before trimmed trailing text.
                     *
                     * EXAMPLE
                     *   span.startOffset       = 10
                     *   endWordIndex           = 4
                     *   spanWords[3].endOffset = end of last kept word
                     *   new endOffset          = 10 + spanWords[3].endOffset
                     */
                    endOffset: trailingNeighbourWords > 0 ? span.startOffset + spanWords[firstTrimmedTrailingWordIndex - 1].endOffset : span.endOffset,
                },
                startAfterWord: trimmedLeadingEdgeWord,
                endBeforeWord: trimmedTrailingEdgeWord,
            };
        }

        /**
         * The words to take off each end of the span, once the match at each end is weighed
         * against the other.
         *
         * WHY the two are weighed
         *   A match may run across the redline's own words when they repeat the
         *   neighbour's: own "本契約は日本法に準拠する。" with the neighbour
         *   "本契約に基づく通知は…" merged after it. Matched from the start, the
         *   neighbour's first words answer the own paragraph's "本" "契約", the rest
         *   answer the neighbour itself, and the match takes the whole span with 9 own
         *   words between. Matched from the end, the neighbour takes its own 24 words with
         *   none between. Together they cover the span, and the end that ran across
         *   words the neighbour does not hold is the one to drop.
         *
         * CASES
         *   - the two matches leave a word between them          -> both, as matched
         *   - they meet or overlap, one holds fewer words the
         *     neighbour does not                                -> that one alone, when it leaves a word
         *   - they hold as many                                 -> null: the own words cannot be told
         *                                                          from the neighbour's, so the span stays whole
         *
         * EXAMPLE
         *   span 33 words; start: swallowed 33, 9 not in the neighbour; end: swallowed 24, 0 not in it
         *   33 + 24 = 57 >= 33 -> overlap; the end holds fewer -> trim 24 from the end, keep 9
         */
        function neighbourWordsToTrimFrom({ swallowedAtStart, swallowedAtEnd, spanWordCount }: SwallowedAtBothEnds): NeighbourWordsToTrim | null {
            const matchesLeaveAWordBetweenThem = swallowedAtStart.swallowedWordCount + swallowedAtEnd.swallowedWordCount < spanWordCount;
            /**
             * WHEN the two matches leave at least one word between them, the redline's own
             *   EXAMPLE
             *     span 33 words, start 0, end 24 -> 0 + 24 = 24 < 33, 9 words kept
             *   CONSEQUENCE
             *     both are trimmed as matched, as before any weighing.
             */
            if (matchesLeaveAWordBetweenThem) return { leadingNeighbourWords: swallowedAtStart.swallowedWordCount, trailingNeighbourWords: swallowedAtEnd.swallowedWordCount };
            /**
             * CASES
             *   - the start's match holds fewer words the neighbour does not -> the start alone
             *   - the end's does                                             -> the end alone
             *   - as many                                                    -> neither
             */
            if (swallowedAtStart.wordsNotInNeighbourCount < swallowedAtEnd.wordsNotInNeighbourCount) {
                const startAloneLeavesAWord = swallowedAtStart.swallowedWordCount < spanWordCount;
                /**
                 * CASES
                 *   - the start's match leaves a word -> trim it alone
                 *   - it takes every word             -> null, the span stays whole
                 */
                return startAloneLeavesAWord ? { leadingNeighbourWords: swallowedAtStart.swallowedWordCount, trailingNeighbourWords: 0 } : null;
            }
            if (swallowedAtEnd.wordsNotInNeighbourCount < swallowedAtStart.wordsNotInNeighbourCount) {
                const endAloneLeavesAWord = swallowedAtEnd.swallowedWordCount < spanWordCount;
                /**
                 * CASES
                 *   - the end's match leaves a word -> trim it alone: 24 of 33, 9 own words kept
                 *   - it takes every word           -> null, the span stays whole
                 */
                return endAloneLeavesAWord ? { leadingNeighbourWords: 0, trailingNeighbourWords: swallowedAtEnd.swallowedWordCount } : null;
            }
            return null;
        }
    }

    /** True when one of the bookmarks has an edge on a neighbouring paragraph without covering its text, each bookmark asked on its own (`OoxmlBookmarks.edgesNeedMove`). */
    bookmarkEdgesNeedMove(bookmarkNames: string[]): boolean {
        const rangeBookmarks = new OoxmlBookmarks(OoxmlRange.bodyOf(this.rangeDocument), this.paragraphElements, bookmarkNames);
        return rangeBookmarks.edgesNeedMove((paragraphElement) => new ParagraphModel(paragraphElement));
    }

    /** Swaps paragraph `index` for the redlined paragraph the engine produced; later calls see the replacement. */
    replaceParagraph(index: number, paragraphXml: string): void {
        const paragraph = this.paragraphElements[index];
        const replacementParagraphElement = this.rangeDocument.importNode(OoxmlRange.parseRangeXml(paragraphXml).documentElement, true) as Element;
        paragraph.parentNode?.replaceChild(replacementParagraphElement, paragraph);
        this.paragraphElements[index] = replacementParagraphElement;
    }

    /**
     * Marks paragraph `index`'s paragraph mark deleted by `revision`
     * (`w:pPr/w:rPr/w:del`). Word removes a paragraph on Accept only when its
     * mark carries this record; a paragraph whose runs alone are deleted stays
     * behind as an empty paragraph. The builder calls this once the engine has
     * struck every word of a paragraph the redline deletes whole.
     *
     * The record is written by `ParagraphRevisionMarks.markParagraphMarkDeleted`, which
     * keeps a section break's mark (its section would merge into the next on Accept) and
     * puts a new `w:rPr` before `w:sectPr` and `w:pPrChange`, as CT_PPr orders them.
     */
    markParagraphDeleted(index: number, revision: Revision): void {
        ParagraphRevisionMarks.markParagraphMarkDeleted(this.paragraphElements[index], revision);
    }

    /**
     * Strikes paragraph `index` whole by `revision`: every live run in it, its breaks
     * included, and its paragraph mark (`ParagraphRevisionMarks.markDeleted`).
     *
     * WHY
     *   A paragraph that holds only a break or spacing (a page break set alone between
     *   two chapters) reads as "\n", which the engine's spacing rule reads as nothing to
     *   write, so a card that deleted it struck neither its break nor its mark. Written
     *   with the chapter before it, the mark went and the break stayed, and the next
     *   chapter's header joined the break's paragraph once accepted: "\nOPTIONAL
     *   BACKGROUND | CHAPTER 15…" (R34 item 4, prod card #28, 2026-10-16). Struck whole,
     *   the paragraph leaves nothing once accepted, and the next paragraph keeps its own.
     */
    strikeParagraphWhole(index: number, revision: Revision): void {
        ParagraphRevisionMarks.markDeleted(this.paragraphElements[index], revision);
    }

    /**
     * Inserts a copy of `sourceParagraphXml` marked as a whole inserted
     * paragraph: the paragraph mark via `w:pPr/w:rPr/w:ins`, and every run and
     * hyperlink wrapped in `w:ins`. Word needs both for the paragraph to be
     * accepted or rejected as one. Every revision already inside the source is
     * restamped as this revision's: the paragraph is presented as one insertion
     * by this author, and a foreign `w:ins` nested inside our `w:ins` would be
     * markup Word rejects on Accept.
     */
    insertParagraph({ afterParagraph, sourceParagraphXml, revision }: InsertedParagraphRequest): Element {
        const rangeDocument = this.rangeDocument;
        const restampRevisions = (paragraph: Element): void => {
            for (const descendantElement of Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "*"))) {
                const descendantElementName = elementNameWithoutPrefix(descendantElement);
                const descendantIsRevisionContainer = REVISION_TAGS.has(descendantElementName);
                /**
                 * WHEN the element is not a revision container
                 *   CONSEQUENCE
                 *     Skip it. Only revision elements are restamped to this insertion revision.
                 */
                if (!descendantIsRevisionContainer) continue;
                descendantElement.setAttributeNS(WORDPROCESSINGML_NS, "w:author", revision.author);
                descendantElement.setAttributeNS(WORDPROCESSINGML_NS, "w:date", revision.stampedAt);
            }
        };

        /**
         * CASES
         *   - normal one-paragraph snippet -> [0] is that paragraph
         *   - multi-paragraph source       -> [0] takes only the first paragraph
         */
        const sourceParagraph = OoxmlRange.bodyParagraphs(OoxmlRange.parseRangeXml(sourceParagraphXml))[0];
        const insertedParagraphElement = rangeDocument.importNode(sourceParagraph, true) as Element;
        restampRevisions(insertedParagraphElement);
        /**
         * WHY the one marker every writer uses
         *   This insertion wrote its own marker, appending `w:rPr` after `w:pPrChange` or
         *   `w:sectPr`: `[pStyle, pPrChange, rPr]`, the schema break b90c6d0dc fixed for
         *   deletions (N21, -26's audit DA-F6, 2026-10-18). `ParagraphRevisionMarks`
         *   places it in CT_PPr's order: `[pStyle, rPr, pPrChange]`.
         */
        ParagraphRevisionMarks.markInserted(insertedParagraphElement, revision);
        const documentBodyElement = OoxmlRange.bodyOf(rangeDocument);
        /**
         * WHEN no predecessor paragraph was supplied
         *   CONSEQUENCE
         *     Insert at the front of the body.
         *
         * CASES
         *   - first paragraph exists -> insert before it
         *   - body is empty          -> insertBefore(..., null) appends
         */
        if (afterParagraph === null) {
            const firstBodyParagraphElement = this.paragraphElements[0] ?? null;
            documentBodyElement.insertBefore(insertedParagraphElement, firstBodyParagraphElement);
        }
        /**
         * WHEN a predecessor paragraph was supplied
         *   CONSEQUENCE
         *     Insert the new paragraph immediately after it.
         */
        else afterParagraph.parentNode?.insertBefore(insertedParagraphElement, afterParagraph.nextSibling);
        return insertedParagraphElement;
    }

    /**
     * Each paragraph with every tracked change accepted: deletions and
     * formatting-change records removed, insertions unwrapped, the paragraph
     * mark's insertion record dropped. Used when a redline's paragraphs are
     * inserted whole beside a live paragraph, so the new paragraph carries the
     * redline's result and not its history.
     */
    acceptedParagraphXmls(): string[] {
        const PARAGRAPH_MARK_PARENTS = new Set(["rPr", "pPr"]);
        const xmlSerializer = XmlHost.xmlSerializer();
        return this.paragraphElements.map((paragraph) => {
            const paragraphClone = paragraph.cloneNode(true) as Element;
            for (const containerLocalName of [...DELETION_CONTAINERS, ...PROPERTY_CHANGE_TAGS]) {
                for (const matchedContainerElement of Array.from(paragraphClone.getElementsByTagNameNS(WORDPROCESSINGML_NS, containerLocalName))) matchedContainerElement.parentNode?.removeChild(matchedContainerElement);
            }
            for (const insertionContainerLocalName of INSERTION_CONTAINERS) {
                for (const insertionContainerElement of Array.from(paragraphClone.getElementsByTagNameNS(WORDPROCESSINGML_NS, insertionContainerLocalName))) {
                    const containerParentNode = insertionContainerElement.parentNode;
                    /**
                     * WHEN the insertion node has no element parent
                     *   CONSEQUENCE
                     *     Skip it. Unwrapping only handles normal element containers.
                     */
                    if (!containerParentNode || containerParentNode.nodeType !== 1) continue;
                    const containerParentElementName = elementNameWithoutPrefix(containerParentNode as Element);
                    const containerParentIsParagraphMarkParent = PARAGRAPH_MARK_PARENTS.has(containerParentElementName);
                    /**
                     * WHEN the insertion sits under w:rPr or w:pPr
                     *   CONSEQUENCE
                     *     Delete the node instead of unwrapping it: this record marks the paragraph mark.
                     */
                    if (containerParentIsParagraphMarkParent) {
                        containerParentNode.removeChild(insertionContainerElement);
                        /**
                         * WHEN the paragraph-mark insertion record was removed
                         *   CONSEQUENCE
                         *     Skip the unwrapping logic below.
                         */
                        continue;
                    }
                    while (insertionContainerElement.firstChild) containerParentNode.insertBefore(insertionContainerElement.firstChild, insertionContainerElement);
                    containerParentNode.removeChild(insertionContainerElement);
                }
            }
            return xmlSerializer.serializeToString(paragraphClone);
        });
    }

    /**
     * Compact rendering of the range's revisions for the anchor log:
     * `[+author|text]` for an insertion, `[-author|text]` for a deletion, long
     * plain runs elided, paragraphs separated by a pilcrow. Cheap enough to log
     * on every refresh and readable enough to see what the engine did.
     */
    revisionSkeleton(): string {
        const SKELETON_LIMIT = 1_500;
        const PLAIN_RUN_LIMIT = 40;
        const skeletonSegments: string[] = [];
        this.paragraphElements.forEach((paragraph, index) => {
            /**
             * CASES
             *   - first paragraph      -> no separator
             *   - later paragraph      -> prepend ¶
             */
            if (index > 0) skeletonSegments.push("¶");
            let openChunk: { mark: string; text: string } | null = null;
            for (const textPiece of new ParagraphModel(paragraph).pieces) {
                const revisionMark = textPiece.deletedBy !== null ? `-${textPiece.deletedBy}` : textPiece.insertedBy !== null ? `+${textPiece.insertedBy}` : "";
                /**
                 * WHEN this piece has the same revision mark as the current chunk
                 *   CONSEQUENCE
                 *     Merge its text into the current chunk instead of starting a new one.
                 */
                if (openChunk && openChunk.mark === revisionMark) {
                    openChunk.text += textPiece.text;
                    /**
                     * WHEN the piece was merged into current
                     *   CONSEQUENCE
                     *     Do not flush or replace the current chunk.
                     */
                    continue;
                }
                /**
                 * WHEN the revision state changes and a chunk is open
                 *   CONSEQUENCE
                 *     Emit the finished chunk before starting the next one.
                 */
                if (openChunk) skeletonSegments.push(openChunk.mark ? `[${openChunk.mark}|${openChunk.text}]` : openChunk.text);
                openChunk = { mark: revisionMark, text: textPiece.text };
            }
            /**
             * WHEN the paragraph loop ends and a chunk is still open
             *   CONSEQUENCE
             *     Emit the final chunk. The state-change flush above will not run again.
             */
            if (openChunk) skeletonSegments.push(openChunk.mark ? `[${openChunk.mark}|${openChunk.text}]` : openChunk.text);
        });
        /**
         * CASES
         *   - revision chunk `[...|...]`     -> keep it whole
         *   - plain run within limit          -> keep it whole
         *   - plain run longer than limit     -> keep start and end, elide the middle
         *
         * WHY
         *   Revision markers stay readable only when the full `[+author|text]` or
         *   `[-author|text]` wrapper survives intact.
         *
         * EXAMPLE
         *   plain run length = 60
         *   slice(0, 18)     -> characters 0 through 17
         *   slice(-18)       -> characters 42 through 59
         *   replaced middle  -> 24 characters, shown as `…`
         */
        const revisionSkeletonText = skeletonSegments.map((part) => (part.startsWith("[") || part.length <= PLAIN_RUN_LIMIT ? part : `${part.slice(0, 18)}…${part.slice(-18)}`)).join("");
        /**
         * CASES
         *   - skeleton fits the limit -> return as is
         *   - skeleton exceeds limit  -> keep the first SKELETON_LIMIT chars and add …
         *
         * EXAMPLE
         *   skeleton.length = 1700
         *   SKELETON_LIMIT  = 1500
         *   slice(0, 1500)  -> characters 0 through 1499
         */
        return revisionSkeletonText.length <= SKELETON_LIMIT ? revisionSkeletonText : `${revisionSkeletonText.slice(0, SKELETON_LIMIT)}…`;
    }

    /**
     * The body paragraphs as the `document-paragraphs` web route reports them
     * to the core. `comments` is the document's comment parts, read by the
     * caller from the file or the package, so each paragraph can list the
     * comments its markers anchor; `numbering` is its numbering and styles
     * parts, read the same way, so each paragraph can carry the list label
     * Word paints before it and the level it is numbered at; `outlineLevels` is
     * its styles part, so each paragraph can carry the heading depth Word gives
     * it and the heading builder can read the document's own structure rather
     * than infer one from the typed text. Each paragraph also carries its table
     * cells (`TableStructure`), outermost first, so a table operation can name a
     * row by any paragraph of it, and the heading path it sits under
     * (`DocumentHeadings`), built from the whole list once every other signal is
     * on the paragraphs, so every reader of the list takes the same path.
     */
    documentParagraphs(comments: DocumentComments, numbering: OoxmlNumbering, outlineLevels: OoxmlOutlineLevels): DocumentParagraph[] {
        const paragraphsBeforeHeadingPaths = this.paragraphsWithoutHeadingPaths(comments, numbering, outlineLevels);
        const headingPaths = new DocumentHeadings(paragraphsBeforeHeadingPaths).paths();
        return paragraphsBeforeHeadingPaths.map((paragraph, paragraphIndex) => ({ ...paragraph, headingPath: headingPaths[paragraphIndex] }));
    }

    /** Every paragraph with every per-paragraph signal, before the heading paths that need the whole list. */
    private paragraphsWithoutHeadingPaths(comments: DocumentComments, numbering: OoxmlNumbering, outlineLevels: OoxmlOutlineLevels): Omit<DocumentParagraph, "headingPath">[] {
        const ownedParagraphBookmarkNamesByParagraph = OoxmlBookmarks.paragraphBookmarkNamesByOwner(OoxmlRange.bodyOf(this.rangeDocument), this.paragraphElements);
        const numberingLabels = numbering.labelsByParagraph(this.paragraphElements);
        const numberingListLevels = numbering.listLevelsByParagraph(this.paragraphElements);
        const paragraphOutlineLevels = outlineLevels.levelsByParagraph(this.paragraphElements);
        const tableStructure = new TableStructure(OoxmlRange.bodyOf(this.rangeDocument));
        const tableOfContentsEntries = TableOfContents.entriesOf(this.paragraphElements);
        const hyperlinkTargets = this.hyperlinkTargetsByRelationshipId();
        /**
         * WHEN the document has no table of contents
         *   CONSEQUENCE
         *     no bookmark is looked up: the map is built only for the anchors an entry can name.
         */
        const paragraphBookmarkNameByBookmarkName = tableOfContentsEntries.size === 0
            ? new Map<string, string | null>()
            : OoxmlRange.paragraphBookmarkNameByBookmarkName(this.paragraphElements, ownedParagraphBookmarkNamesByParagraph);
        return this.paragraphElements.map((paragraph, paragraphIndex) => {
            const paragraphModel = new ParagraphModel(paragraph);
            const paragraphIds = OoxmlRange.idsOf(paragraph);
            const ownedParagraphBookmarkNames = ownedParagraphBookmarkNamesByParagraph.get(paragraph) ?? [];
            return {
                paragraphIndex,
                allChangesAcceptedText: paragraphModel.text,
                allChangesRejectedText: paragraphModel.allChangesRejectedText,
                trackedChangesText: paragraphModel.trackedChangesText(),
                agentViewText: paragraphModel.agentViewText(),
                isAutoNumbered: OoxmlRange.isAutoNumbered(paragraph),
                numberingLabel: numberingLabels[paragraphIndex],
                outlineLevel: paragraphOutlineLevels[paragraphIndex],
                listLevel: numberingListLevels[paragraphIndex],
                paraId: paragraphIds.paraId,
                textId: paragraphIds.textId,
                bookmarkNames: OoxmlRange.bookmarkNamesOf(paragraph, ownedParagraphBookmarkNames),
                /**
                 * CASES
                 *   - the paragraph owns one or more paragraph bookmarks -> the first, in document order of their starts
                 *   - it owns none                                       -> null, and no operation can name it
                 *
                 * WHY the first
                 *   A paragraph owns two names only where an older name survived a
                 *   join; the naming pass leaves such a paragraph alone, and the
                 *   earliest start is the one a reader of the markup sees first.
                 */
                paragraphBookmarkName: ownedParagraphBookmarkNames[0] ?? null,
                leadingClauseMarker: ClauseMarker.leading(paragraphModel.text)?.trim() ?? null,
                comments: comments.forReferenceIds(paragraphModel.commentReferenceIds),
                pendingChanges: OoxmlRange.pendingChangesOf(paragraphModel),
                formattingChanges: paragraphModel.formattingChanges(),
                uneditableSpans: OoxmlRange.uneditableSpansOf(paragraphModel),
                links: OoxmlRange.linksShownOf(paragraphModel, tableOfContentsEntries.has(paragraph), hyperlinkTargets),
                tableOfContentsEntry: OoxmlRange.tableOfContentsEntryOf(paragraph, tableOfContentsEntries, paragraphBookmarkNameByBookmarkName),
                tableCellPath: tableStructure.cellPathOf(paragraph),
            };
        });
    }

    /**
     * The paragraph's pending changes as the reader route lists them: each change's
     * id, kind, author and date as `ParagraphModel.pendingChanges` reads them, its
     * words, and where it sits in the accepted text.
     *
     * EXAMPLE
     *   `Pay within <del id="c1" author="Bo">thirty</del><ins id="c2" author="Ana">sixty</ins> days`
     *   c1 -> del by Bo, "thirty", offsets 11..11  (struck words claim no accepted characters)
     *   c2 -> ins by Ana, "sixty", offsets 11..16
     *
     * CASES
     *   - the change holds words -> listed, from its first piece's start to its last piece's end
     *   - it holds none          -> left out: an inserted or struck picture has no words or place
     *                               in the accepted text to report
     */
    private static pendingChangesOf(paragraphModel: ParagraphModel): ParagraphPendingChange[] {
        return paragraphModel.pendingChanges().flatMap((change) => {
            const firstPiece = change.pieces[0];
            /**
             * EXAMPLE
             *   change.pieces.length = 3
             *   length - 1           -> 2, the piece whose end closes the change
             */
            const lastPiece = change.pieces[change.pieces.length - 1];
            /**
             * WHEN the change holds no text piece
             *   CONSEQUENCE
             *     leave it out, as the docblock's CASES say.
             */
            if (firstPiece === undefined || lastPiece === undefined) return [];
            return [{
                id: change.id,
                kind: change.kind,
                author: change.author,
                date: change.date,
                text: change.pieces.map((piece) => piece.text).join(""),
                startOffset: firstPiece.startOffset,
                endOffset: lastPiece.endOffset,
            }];
        });
    }

    /**
     * The links the reader reports for one paragraph (`ParagraphLinks.readingOf`).
     *
     * CASES
     *   - a table of contents entry -> []: its link goes to the heading it lists, Word's to keep;
     *                                  an edit named on the entry is moved onto that heading
     *   - any other paragraph       -> every link that holds live text
     */
    private static linksShownOf(paragraphModel: ParagraphModel, isTableOfContentsEntry: boolean, hyperlinkTargets: HyperlinkTargetsByRelationshipId): ParagraphLink[] {
        /** EMPTY: a table of contents entry's links are Word's own jumps to its headings, which the agent never changes. */
        if (isTableOfContentsEntry) return [];
        return ParagraphLinks.readingOf(paragraphModel, hyperlinkTargets);
    }

    /**
     * Every bookmark name a body paragraph carries, mapped to that paragraph's
     * paragraph bookmark, so a table of contents entry's `_Toc…` anchor resolves to
     * the heading's paragraph_id in one lookup.
     *
     * CASES
     *   - the carrying paragraph owns a paragraph bookmark -> that name
     *   - it owns none (a raw upload no naming pass reached) -> null
     *
     * EDGE CASE
     *   A start Word placed at body level, just before the heading, is carried by no
     *   paragraph and is absent from the map; the entry then names a heading the
     *   reader cannot place and its target is null.
     */
    private static paragraphBookmarkNameByBookmarkName(paragraphs: Element[], ownedParagraphBookmarkNamesByParagraph: Map<Element, string[]>): Map<string, string | null> {
        const byBookmarkName = new Map<string, string | null>();
        for (const paragraph of paragraphs) {
            const ownedParagraphBookmarkName = ownedParagraphBookmarkNamesByParagraph.get(paragraph)?.[0] ?? null;
            for (const bookmarkName of OoxmlBookmarks.namesIn(paragraph)) byBookmarkName.set(bookmarkName, ownedParagraphBookmarkName);
        }
        return byBookmarkName;
    }

    /**
     * What the reader reports about a paragraph's place in a table of contents.
     *
     * CASES
     *   - neither inside a `TOC` field nor a line of a linked contents list        -> null
     *   - an entry whose anchor names a paragraph with a paragraph bookmark       -> that bookmark as the target, and how the entry is listed
     *   - an entry whose anchor names nothing the body carries, or that has none  -> an entry whose target is null
     */
    private static tableOfContentsEntryOf(paragraph: Element, entries: Map<Element, TableOfContentsEntryTarget>, paragraphBookmarkNameByBookmarkName: Map<string, string | null>): TableOfContentsEntry | null {
        const entry = entries.get(paragraph);
        if (entry === undefined) return null;
        const targetParagraphBookmarkName = entry.anchorBookmarkName === null ? null : (paragraphBookmarkNameByBookmarkName.get(entry.anchorBookmarkName) ?? null);
        return { targetParagraphBookmarkName, listedBy: entry.listedBy };
    }

    /**
     * The stretches of the paragraph's accepted text that Word owns (field
     * results, field instructions, equations), adjacent uneditable pieces joined
     * into one span, so the reader can show them marked and the apply route can
     * refuse an anchor that covers one.
     *
     * EXAMPLE
     *   accepted pieces (offsets, editable): 0-4 "See " yes, 4-11 "Section" no, 11-16 " 7.19" no, 16-29 " for details." yes
     *   -> [{ startOffset: 4, endOffset: 16, text: "Section 7.19" }]
     *
     * CASES
     *   - no uneditable piece                                   -> []
     *   - two uneditable pieces end to end                      -> one span (a field result written as several runs)
     *   - uneditable pieces with editable text between them     -> one span each
     */
    private static uneditableSpansOf(paragraphModel: ParagraphModel): UneditableSpan[] {
        const spans: UneditableSpan[] = [];
        for (const piece of paragraphModel.acceptedPieces()) {
            if (piece.editable) continue;
            const lastSpan = spans[spans.length - 1];
            /**
             * WHEN this piece starts exactly where the last span ends
             *   CONSEQUENCE
             *     it belongs to the same field: the span grows to the piece's end.
             */
            if (lastSpan !== undefined && lastSpan.endOffset === piece.startOffset) {
                lastSpan.endOffset = piece.endOffset;
                lastSpan.text = paragraphModel.text.slice(lastSpan.startOffset, lastSpan.endOffset);
                continue;
            }
            spans.push({ startOffset: piece.startOffset, endOffset: piece.endOffset, text: paragraphModel.text.slice(piece.startOffset, piece.endOffset) });
        }
        return spans;
    }

    /**
     * Names the document under `naming`: strips every paragraph bookmark of a
     * scheme `naming` retires, then gives every body paragraph that owns no
     * paragraph bookmark one of its own, so the engine can address it: a
     * `w:bookmarkStart` right after the paragraph properties (first when there
     * are none) and a `w:bookmarkEnd` as the last child, with a fresh name from
     * `naming` and the document's next free bookmark id.
     *
     * Ownership is read with `OoxmlBookmarks.paragraphBookmarkNamesByOwner`,
     * the rule `documentParagraphs` reports names with, so a paragraph named
     * by the add-in or by an earlier run of this method is left alone whichever
     * of Word's marker placements it has, and running this twice changes
     * nothing. Every name minted is checked against every bookmark name already
     * in the document, so two paragraphs never share a name.
     *
     * CASES
     *   - the document carries retired-scheme names      -> all removed first, then every paragraph named
     *   - the paragraph owns a paragraph bookmark        -> untouched
     *   - the paragraph owns none                        -> one start and one end added
     *   - every paragraph already owns one, none retired -> nothing removed, nothing added, the document unchanged
     *
     * EXAMPLE
     *   three paragraphs, the first named `_dioptra_p_3f59…`, the others unnamed, bookmark ids in use up to 466
     *   -> `_dioptra_p_3f59…` and its end are removed
     *   -> each paragraph gets <w:bookmarkStart w:id="467|468|469" w:name="_result_prev_signed"/> … <w:bookmarkEnd/>
     *   -> { removedLegacyNames: ["_dioptra_p_3f59…"], addedNames: ["_result_prev_signed", …, …] }
     */
    nameUnnamedParagraphs(): ParagraphNamingResult {
        const rangeDocument = this.rangeDocument;
        const removedLegacyNames = OoxmlBookmarks.removeRetiredParagraphBookmarks(rangeDocument.documentElement);
        const ownedParagraphBookmarkNamesByParagraph = OoxmlBookmarks.paragraphBookmarkNamesByOwner(OoxmlRange.bodyOf(rangeDocument), this.paragraphElements);
        const bookmarkNamesInUse = new Set(OoxmlBookmarks.namesIn(rangeDocument.documentElement));
        let nextBookmarkId = OoxmlBookmarks.nextBookmarkId(rangeDocument.documentElement);
        const addedNames: string[] = [];
        for (const paragraph of this.paragraphElements) {
            const paragraphAlreadyNamed = (ownedParagraphBookmarkNamesByParagraph.get(paragraph) ?? []).length > 0;
            /**
             * WHEN the paragraph already owns a paragraph bookmark
             *   CONSEQUENCE
             *     Leave it alone. A second name on one paragraph is what this method exists to avoid.
             */
            if (paragraphAlreadyNamed) continue;
            const bookmarkName = freshParagraphBookmarkName(bookmarkNamesInUse);
            bookmarkNamesInUse.add(bookmarkName);
            OoxmlRange.layBookmarkOverParagraph(rangeDocument, paragraph, bookmarkName, nextBookmarkId);
            addedNames.push(bookmarkName);
            nextBookmarkId += 1;
        }
        return { removedLegacyNames, addedNames };
    }

    /**
     * Puts back, zero-width at the start of its paragraph, every bookmark Word left
     * out of this read although Office.js resolves it into one of its paragraphs.
     *
     * WHY
     *   Word Online's `getOoxml()` of an empty paragraph drops the zero-width
     *   bookmark that names it, while `getBookmarkRangeOrNullObject` still resolves
     *   the name into that paragraph. Every reader of the read (the fold's zoning,
     *   the merge writer, the per-cell writer) finds a card's paragraph by its
     *   bookmark, so a card on an empty cell found none and wrote nothing.
     *
     * CASES, per name
     *   - the read already carries a marker of the name -> left as Word returned it
     *   - it carries none                                -> a start and an end laid on the paragraph at the
     *                                                       given index, with the next id free in the read
     *
     * EXAMPLE
     *   Word Online read of the empty Annual cell   <w:p w:rsidR="00ED02A7"/>
     *   paragraphIndexByBookmarkName                { _leg_letter_wire -> 0 }
     *   -> <w:p w:rsidR="00ED02A7"><w:bookmarkStart w:id="1" w:name="_leg_letter_wire"/><w:bookmarkEnd w:id="1"/></w:p>
     *   -> returns ["_leg_letter_wire"]
     *
     * RETURNS
     *   the names put back, in the map's order.
     */
    restoreDroppedBookmarks(paragraphIndexByBookmarkName: ReadonlyMap<string, number>): string[] {
        const rangeDocument = this.rangeDocument;
        const bookmarkNamesInRead = this.bookmarkNamesCarried();
        let nextBookmarkId = OoxmlBookmarks.nextBookmarkId(rangeDocument.documentElement);
        const restoredNames: string[] = [];
        for (const [bookmarkName, paragraphIndex] of paragraphIndexByBookmarkName) {
            /**
             * WHEN the read already carries a marker of this name
             *   CONSEQUENCE
             *     Leave it where Word put it; only a name Word dropped is laid.
             */
            if (bookmarkNamesInRead.has(bookmarkName)) continue;
            OoxmlRange.layBookmarkOverParagraph(rangeDocument, this.paragraphElements[paragraphIndex], bookmarkName, nextBookmarkId);
            restoredNames.push(bookmarkName);
            nextBookmarkId += 1;
        }
        return restoredNames;
    }

    /**
     * The name of every bookmark whose start this range carries: whether the read holds
     * a marker of a name at all.
     *
     * WHY the starts alone, and not the paragraph each answers to
     *   The question is whether the paragraph a bookmark names is still in the read, and
     *   a start present anywhere says it is. Asking which paragraph owns each name
     *   (`paragraphElementsByBookmarkName`) walks every paragraph's markers, and Apply
     *   builds every card over its read: a hundred cards spent twice as long building.
     *
     * EXAMPLE
     *   <w:p><w:bookmarkStart w:name="_pay"/>…</w:p>   -> {"_pay"}
     */
    bookmarkNamesCarried(): Set<string> {
        return new Set(OoxmlBookmarks.namesIn(this.rangeDocument.documentElement));
    }

    /**
     * Lays a bookmark over one paragraph of this range, with the next id free in it.
     *
     * WHY
     *   A paragraph the user deleted and a card writes back carries the card
     *   paragraph's bookmark (`ParagraphInsertion.carriedBookmarkName`), so every
     *   reader that finds the card's paragraph by its bookmark finds the one written.
     *
     * EXAMPLE
     *   range holding _pay with bookmark id 1, the written-back paragraph "The term is two years."
     *   -> <w:p>…<w:bookmarkStart w:id="2" w:name="_term"/>…<w:bookmarkEnd w:id="2"/></w:p>
     */
    layBookmarkOnParagraph(paragraph: Element, bookmarkName: string): void {
        OoxmlRange.layBookmarkOverParagraph(this.rangeDocument, paragraph, bookmarkName, OoxmlBookmarks.nextBookmarkId(this.rangeDocument.documentElement));
    }

    /**
     * Lays one bookmark's start at the beginning of a paragraph's content and its
     * end as the paragraph's last child.
     *
     * CASES, for where the start goes
     *   - the paragraph has w:pPr   -> right after it, where Word writes a bookmark that begins at the first character
     *   - no w:pPr                  -> first child (insertBefore a null first child appends to an empty paragraph)
     */
    private static layBookmarkOverParagraph(rangeDocument: Document, paragraph: Element, bookmarkName: string, bookmarkId: number): void {
        const bookmarkStartElement = rangeDocument.createElementNS(WORDPROCESSINGML_NS, "w:bookmarkStart");
        bookmarkStartElement.setAttributeNS(WORDPROCESSINGML_NS, "w:id", String(bookmarkId));
        bookmarkStartElement.setAttributeNS(WORDPROCESSINGML_NS, "w:name", bookmarkName);
        const bookmarkEndElement = rangeDocument.createElementNS(WORDPROCESSINGML_NS, "w:bookmarkEnd");
        bookmarkEndElement.setAttributeNS(WORDPROCESSINGML_NS, "w:id", String(bookmarkId));
        const paragraphProperties = Array.from(paragraph.childNodes).find((node): node is Element => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === "pPr");
        if (paragraphProperties === undefined) paragraph.insertBefore(bookmarkStartElement, paragraph.firstChild);
        else paragraph.insertBefore(bookmarkStartElement, paragraphProperties.nextSibling);
        paragraph.appendChild(bookmarkEndElement);
    }

    /**
     * The bookmark names a paragraph answers to: the paragraph bookmarks it
     * owns under the starts-only rule, then every other bookmark whose start
     * sits inside it (a user's own, Word's `_GoBack`).
     *
     * WHY
     *   A paragraph bookmark whose start Word placed at body level before the
     *   paragraph, or in the previous paragraph's tail, is this paragraph's id
     *   although no marker of it is inside; and a start inside this paragraph
     *   after all of its text names the next paragraph, not this one. Listing
     *   by marker position alone got both cases wrong.
     */
    private static bookmarkNamesOf(paragraph: Element, ownedParagraphBookmarkNames: string[]): string[] {
        const otherBookmarkNames = OoxmlBookmarks.namesIn(paragraph).filter((bookmarkName) => !isParagraphBookmarkName(bookmarkName));
        return [...ownedParagraphBookmarkNames, ...otherBookmarkNames];
    }

    serialize(): string {
        return XmlHost.xmlSerializer().serializeToString(this.rangeDocument);
    }

    /** The addresses this range's `w:hyperlink` elements point to, from its package's relationships (`hyperlinkTargetsOf`). */
    hyperlinkTargetsByRelationshipId(): HyperlinkTargetsByRelationshipId {
        return hyperlinkTargetsOf(this.rangeDocument);
    }

    /**
     * Leaves out the markers of every comment this range anchors at one end only,
     * for a range about to be handed to Word as a replacement.
     *
     * WHY
     *   A comment whose highlighted range crosses a paragraph break keeps its
     *   other boundary marker in a paragraph outside this range. Re-inserting the
     *   half-range makes Word materialize a second copy of a comment the document
     *   still holds. A range that covers the whole document never has one, so this
     *   is the caller's call and not `serialize`'s: on a whole-document write it
     *   would strip markers the document is entitled to keep.
     */
    dropCommentMarkersAnchoredOutsideThisRange(): void {
        dropHalfAnchoredCommentMarkers(OoxmlRange.bodyOf(this.rangeDocument));
    }

    /**
     * Whether Word numbers this paragraph from a list definition: a `w:numPr`
     * under the paragraph's own `w:pPr`. Numbering inside a nested table or
     * content control belongs to those paragraphs, so only the direct
     * properties block is consulted.
     */
    private static isAutoNumbered(paragraph: Element): boolean {
        const paragraphProperties = Array.from(paragraph.childNodes).find((node): node is Element => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === "pPr");
        /**
         * WHEN the paragraph has no properties block
         *   CONSEQUENCE
         *     it cannot carry numbering.
         */
        if (paragraphProperties === undefined) return false;
        return paragraphProperties.getElementsByTagNameNS(WORDPROCESSINGML_NS, "numPr").length > 0;
    }

    private static idsOf(paragraph: Element): ParagraphIds {
        const paragraphIdAttribute = (name: string): string | null =>
            paragraph.getAttributeNS(WORDML_2010_NS, name) ?? paragraph.getAttribute(`w14:${name}`) ?? null;
        return { paraId: paragraphIdAttribute("paraId") || null, textId: paragraphIdAttribute("textId") || null };
    }
}
